import { useState, useCallback, useEffect, useRef } from 'react';
import * as Cesium from 'cesium';
import { elevationService } from '../services/elevationService';
import { DEFAULT_PATH_COLOR } from '../components/PathTool/types';
import { calculateBufferPolygonsAreaM2 } from '../utils/geometryUtils';
import { buildBufferHierarchy } from '../utils/cesiumHelpers';
import { formatDistance } from '../utils/units';
import { usePickTerrainPosition, useConstraintCheck } from './common/useCesiumInteraction';
import { useCustomDataSource } from './common/useCustomDataSource';
import { disableCameraControls, enableCameraControls } from '../utils/cesiumHelpers';
import { generateId } from '../utils/idGeneration';
import { useVisibilityControl } from './useVisibilityControl';
import { useLocalStoragePersistence } from './useLocalStoragePersistence';
import { useEventSubscribe } from '../events/hooks';
import { createPathPoint, createPathLine, createPathLabel, createGlowHighlight, makePolylineWidthProperty } from '../utils/cesiumEntityFactory';
import { combinedElevationProfiles, fetchSegmentElevationProfile as fetchSegmentElevation } from './paths/pathElevation';
import { STORAGE_KEY, serializePathsForStorage, deserializeStoredPaths } from './paths/pathSerialization';
const generatePointId = () => generateId('point');
const generateSegmentId = () => generateId('segment');
const generatePathId = () => generateId('path');
// Next default name is one past the highest existing "Path N", so the sequence
// keeps climbing (Path 1 → Path 2 → Path 3) and never collides or goes backward
// when earlier paths are deleted. Counting paths.size regressed on delete.
const nextDefaultPathName = (paths) => {
    const numbers = [...paths.values()]
        .map(p => { var _a; return (_a = /^Path (\d+)$/.exec(p.name)) === null || _a === void 0 ? void 0 : _a[1]; })
        .filter((n) => Boolean(n))
        .map(Number);
    return `Path ${(numbers.length ? Math.max(...numbers) : 0) + 1}`;
};
export const usePaths = ({ viewer, isActive, projectId, constraintPolygon, editingPathId, viewLocked, readyLayers }) => {
    const viewLockedRef = useRef(!!viewLocked);
    useEffect(() => { viewLockedRef.current = !!viewLocked; }, [viewLocked]);
    const [paths, setPaths] = useState(new Map());
    const [pendingPath, setPendingPath] = useState(null);
    const [selectedPath, setSelectedPath] = useState(null);
    // Dialog visibility, decoupled from selection: X on the dialog while the
    // path has a popped-out profile hides the dialog but keeps the selection
    // (and so the editability). See closePathDialog.
    const [pathDialogHidden, setPathDialogHidden] = useState(false);
    const [hoveredPath, setHoveredPath] = useState(null);
    const [dragging, setDragging] = useState(null);
    const [poppedOutProfiles, setPoppedOutProfiles] = useState([]);
    const [defaultUnit, setDefaultUnit] = useState(() => {
        try {
            const prefs = JSON.parse(localStorage.getItem('landscope_unit_preferences') || '{}');
            if (prefs.distanceUnit === 'm' || prefs.distanceUnit === 'ft')
                return prefs.distanceUnit;
        }
        catch ( /* ignore */_a) { /* ignore */ }
        return 'ft';
    });
    const [showSegmentLabels, setShowSegmentLabels] = useState(false);
    const [highlightedSegment, setHighlightedSegment] = useState(null);
    const highlightedSegmentRef = useRef(null);
    const [draggingPending, setDraggingPending] = useState(null);
    const [extendingPath, setExtendingPath] = useState(null);
    const [selectedDrawingVertex, setSelectedDrawingVertex] = useState(null);
    const [hoveredVertex, setHoveredVertex] = useState(null);
    // Which end of the selected path subsequent terrain clicks should extend.
    // Set by clicking an endpoint OR by the first extend (sticky to that end thereafter).
    // Null → fall back to nearest-end heuristic on next click.
    const [extendAnchorEnd, setExtendAnchorEnd] = useState(null);
    const [itemOpacities, setItemOpacities] = useState(new Map());
    const dataSourceRef = useCustomDataSource(viewer, 'paths');
    const elevationDebounceRef = useRef(null);
    const elevationFetchGenRef = useRef(0);
    const glowEntityRef = useRef(null);
    const mouseDownPosRef = useRef(null);
    // Refs for handler stability (avoid recreating Cesium event handlers on every state change)
    const draggingRef = useRef(null);
    const draggingPendingRef = useRef(null);
    const extendingPathRef = useRef(null);
    const pathsRef = useRef(paths);
    const pendingPathRef = useRef(null);
    const showSegmentLabelsRef = useRef(false);
    const defaultUnitRef = useRef(defaultUnit);
    const editingPathIdRef = useRef(editingPathId !== null && editingPathId !== void 0 ? editingPathId : null);
    const selectedDrawingVertexRef = useRef(null);
    const hoveredVertexRef = useRef(null);
    const selectedPathRef = useRef(null);
    const extendAnchorEndRef = useRef(null);
    const isActiveRef = useRef(isActive);
    // Tracks whether the in-progress drag actually moved past the click threshold.
    // If false at mouseUp, treat the gesture as a click → select-toggle the vertex.
    const dragMovedRef = useRef(false);
    // Phantom-node-on-segment state. While the cursor hovers over a path
    // segment line (and no vertex is hovered), we show a translucent "ghost"
    // node at the cursor's terrain projection to signal "click to insert a
    // node here." Clicking the phantom routes to insertNodeOnSegment.
    const phantomNodeRef = useRef(null);
    const phantomTargetRef = useRef(null);
    useEffect(() => { selectedDrawingVertexRef.current = selectedDrawingVertex; }, [selectedDrawingVertex]);
    useEffect(() => { hoveredVertexRef.current = hoveredVertex; }, [hoveredVertex]);
    useEffect(() => { selectedPathRef.current = selectedPath; }, [selectedPath]);
    useEffect(() => { extendAnchorEndRef.current = extendAnchorEnd; }, [extendAnchorEnd]);
    useEffect(() => { isActiveRef.current = isActive; }, [isActive]);
    // Node markers (the draggable handles) are shown only while a path is actively
    // editable: it's the selected path, or the path in an active server-edit
    // session (whose dialog may be closed — editing continues from the popped-out
    // profile), AND not in read-only view mode. Every other finished path reads as
    // a clean line with no handles. Lines themselves are never touched here, so
    // the path stays visible regardless. (Drawing-in-progress nodes live on
    // pendingPath, not here, so they're unaffected.)
    useEffect(() => {
        const selId = selectedPath === null || selectedPath === void 0 ? void 0 : selectedPath.pathId;
        paths.forEach((path, id) => {
            const showHandles = (id === selId || id === editingPathId) && !viewLocked;
            path.points.forEach(pt => { if (pt.entity)
                pt.entity.show = showHandles; });
        });
    }, [viewLocked, selectedPath === null || selectedPath === void 0 ? void 0 : selectedPath.pathId, paths, editingPathId]);
    // Undo / redo of GEOMETRY edits (vertex move/insert/delete, extend, create,
    // delete). Each stack entry is a full geometry snapshot (PathConfig[]); the
    // pre-change state is pushed at each edit site, and undo/redo rebuild the
    // paths from a snapshot. Geometry only — async elevation profiles and colour
    // changes don't record. Mirrors the polygon tool 1:1.
    const undoStackRef = useRef([]);
    const redoStackRef = useRef([]);
    const UNDO_LIMIT = 50;
    const captureConfigs = useCallback(() => {
        const out = [];
        pathsRef.current.forEach((p) => {
            out.push({
                id: p.id,
                name: p.name,
                points: p.points.map(pt => {
                    const c = Cesium.Cartographic.fromCartesian(pt.position);
                    return { latitude: Cesium.Math.toDegrees(c.latitude), longitude: Cesium.Math.toDegrees(c.longitude) };
                }),
                color: p.color,
                unit: p.displayUnit,
                projectId: p.projectId,
                showMeasurements: p.showMeasurements,
                lineWidth: p.lineWidth,
                colorBy: p.colorBy,
            });
        });
        return out;
    }, []);
    const pushUndoSnapshot = useCallback(() => {
        undoStackRef.current.push(captureConfigs());
        if (undoStackRef.current.length > UNDO_LIMIT)
            undoStackRef.current.shift();
        redoStackRef.current = [];
    }, [captureConfigs]);
    // Clear hover highlight when path tool deactivates so other tools' interactions
    // aren't visually overridden.
    useEffect(() => {
        if (!isActive)
            setHoveredVertex(null);
    }, [isActive]);
    useEffect(() => { draggingRef.current = dragging; }, [dragging]);
    useEffect(() => { draggingPendingRef.current = draggingPending; }, [draggingPending]);
    useEffect(() => { extendingPathRef.current = extendingPath; }, [extendingPath]);
    useEffect(() => { pathsRef.current = paths; }, [paths]);
    useEffect(() => { pendingPathRef.current = pendingPath; }, [pendingPath]);
    useEffect(() => { editingPathIdRef.current = editingPathId !== null && editingPathId !== void 0 ? editingPathId : null; }, [editingPathId]);
    useEffect(() => { showSegmentLabelsRef.current = showSegmentLabels; }, [showSegmentLabels]);
    useEffect(() => { defaultUnitRef.current = defaultUnit; }, [defaultUnit]);
    // Live measurements visibility. Segment labels are always created now; this
    // flips their visibility at the LABEL level so it composes cleanly with the
    // per-path visibility control (entity-level show) — a label shows only when
    // its path is visible AND measurements are on. The in-progress (pending) path
    // follows the global draw-time toggle; each FINISHED path follows its own
    // per-path showMeasurements flag (toggled from the options dialog).
    useEffect(() => {
        const apply = (seg, show) => {
            var _a;
            if ((_a = seg.labelEntity) === null || _a === void 0 ? void 0 : _a.label) {
                seg.labelEntity.label.show = new Cesium.ConstantProperty(show);
            }
        };
        // Per-path: each path shows the distance on ALL of its own segments based on
        // its own flag. The in-progress (pending) path follows the draw-HUD toggle
        // — that toggle IS the pending path's measurements switch, and its value is
        // captured onto the path when it's finished. No cross-path coupling: toggling
        // one path never touches another.
        paths.forEach(p => p.segments.forEach(seg => apply(seg, !!p.showMeasurements)));
        pendingPath === null || pendingPath === void 0 ? void 0 : pendingPath.segments.forEach(seg => apply(seg, showSegmentLabels));
    }, [showSegmentLabels, paths, pendingPath]);
    // Keep the in-progress path's segment labels in the current unit. Their text is
    // baked at creation, so when the unit changes (draw-HUD picker / global metric
    // pref, synced into defaultUnit) we re-stamp every pending label so the on-map
    // distances match the panel instead of being stuck in the creation-time unit.
    useEffect(() => {
        pendingPath === null || pendingPath === void 0 ? void 0 : pendingPath.segments.forEach(seg => {
            var _a;
            if ((_a = seg.labelEntity) === null || _a === void 0 ? void 0 : _a.label) {
                seg.labelEntity.label.text = new Cesium.ConstantProperty(formatDistance(seg.distanceMeters, defaultUnit));
            }
        });
    }, [defaultUnit, pendingPath]);
    // Function refs for handler stability
    const pickTerrainPositionRef = useRef(() => null);
    const isPositionInConstraintRef = useRef(() => true);
    const createPointEntityRef = useRef(() => null);
    const createLineEntityRef = useRef(() => null);
    const createLabelEntityRef = useRef(() => null);
    const deleteNodeRef = useRef(null);
    const cancelPendingRef = useRef(null);
    const addPointToPathRef = useRef(() => null);
    const checkExistingPathClickRef = useRef(() => false);
    const insertNodeOnSegmentRef = useRef(() => null);
    const insertNodeOnPendingSegmentRef = useRef(() => null);
    const updateDraggedPointRef = useRef(() => { });
    const updateDraggedPendingPointRef = useRef(() => { });
    const finishPathRef = useRef(() => undefined);
    const getPathEntities = useCallback((p) => {
        const entities = [];
        // Segment lines first: useVisibilityControl treats the first defined entity as
        // the item's visibility proxy. Read-only view mode hides the node markers (edit
        // affordances), so a node-first order made isItemVisible report a viewed path as
        // "invisible" — deselecting it and flickering its dialog shut on open, and
        // mis-marking it inactive in the layer list. The line reflects true visibility.
        p.segments.forEach(seg => {
            entities.push(seg.lineEntity);
            entities.push(seg.labelEntity);
        });
        p.points.forEach(pt => entities.push(pt.entity));
        if (p.totalLabelEntity)
            entities.push(p.totalLabelEntity);
        if (p.bufferEntities)
            entities.push(...p.bufferEntities);
        return entities;
    }, []);
    const { isVisible, hideAll, showAll, toggleItemVisibility, isItemVisible } = useVisibilityControl(paths, getPathEntities);
    const prevProjectIdRef = useRef(null);
    useEffect(() => {
        if (prevProjectIdRef.current !== null && prevProjectIdRef.current !== projectId) {
            if (dataSourceRef.current) {
                dataSourceRef.current.entities.removeAll();
            }
            setPaths(new Map());
            setPendingPath(null);
            setSelectedPath(null);
            setHoveredPath(null);
            setDragging(null);
            setDraggingPending(null);
            setExtendingPath(null);
            undoStackRef.current = [];
            redoStackRef.current = [];
        }
        prevProjectIdRef.current = projectId;
    }, [projectId]);
    const getPointType = (isFirst, isLast) => {
        if (isFirst)
            return 'first';
        if (isLast)
            return 'last';
        return 'middle';
    };
    // Lazily (re)creates the phantom-node entity in the current data source.
    // Lazy because the projectId-change effect calls `entities.removeAll()`,
    // which would silently drop a pre-created phantom; this guarantees a
    // valid entity whenever a hover wants to show one.
    const ensurePhantomEntity = useCallback(() => {
        if (!dataSourceRef.current)
            return null;
        const existing = phantomNodeRef.current;
        if (existing && dataSourceRef.current.entities.contains(existing)) {
            return existing;
        }
        const phantom = dataSourceRef.current.entities.add({
            show: false,
            position: new Cesium.ConstantPositionProperty(Cesium.Cartesian3.ZERO),
            point: {
                pixelSize: 11,
                color: Cesium.Color.WHITE.withAlpha(0.5),
                outlineColor: Cesium.Color.BLACK.withAlpha(0.6),
                outlineWidth: 2,
                heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            },
        });
        phantomNodeRef.current = phantom;
        return phantom;
    }, []);
    const hidePhantom = useCallback(() => {
        phantomTargetRef.current = null;
        if (phantomNodeRef.current)
            phantomNodeRef.current.show = false;
    }, []);
    // Hide phantom whenever the path tool deactivates so it doesn't linger.
    useEffect(() => {
        if (!isActive)
            hidePhantom();
    }, [isActive, hidePhantom]);
    const createPointEntity = useCallback((position, color, isFirst, isLast) => {
        if (!dataSourceRef.current)
            return null;
        return createPathPoint(dataSourceRef.current, position, color, getPointType(isFirst, isLast));
    }, []);
    const createLineEntity = useCallback((start, end, color, width) => {
        if (!dataSourceRef.current)
            return null;
        return createPathLine(dataSourceRef.current, [start, end], color, 1.0, width, viewer !== null && viewer !== void 0 ? viewer : undefined);
    }, [viewer]);
    const createLabelEntity = useCallback((start, end, distance, unit) => {
        if (!dataSourceRef.current)
            return null;
        const midpoint = Cesium.Cartesian3.midpoint(start, end, new Cesium.Cartesian3());
        const text = formatDistance(distance, unit);
        const entity = createPathLabel(dataSourceRef.current, midpoint, text);
        // Segment labels are always created now; the "Show measurements" toggle
        // drives their visibility (live, via the effect below). Seed the initial
        // show from the current flag so off-state labels don't flash in.
        if (entity.label)
            entity.label.show = new Cesium.ConstantProperty(showSegmentLabelsRef.current);
        return entity;
    }, []);
    const pickTerrainPosition = usePickTerrainPosition(viewer);
    const isPositionInConstraint = useConstraintCheck(constraintPolygon);
    const fetchSegmentElevationProfile = useCallback(async (startPos, endPos, gen) => {
        if (!projectId)
            return null;
        const profile = await fetchSegmentElevation(projectId, startPos, endPos, readyLayers);
        if (gen !== elevationFetchGenRef.current)
            return null;
        return profile;
    }, [projectId, readyLayers]);
    // When a previously-not-ready analytical layer finishes processing, refetch
    // the profiles of already-drawn paths so the now-available layer fills in —
    // otherwise its colour-by option un-greys but the cached profile has no values
    // for it (blank chart) until the user redraws.
    const readyLayersSig = (readyLayers !== null && readyLayers !== void 0 ? readyLayers : []).slice().sort().join(',');
    const prevReadyLayersSigRef = useRef(readyLayersSig);
    useEffect(() => {
        const prev = prevReadyLayersSigRef.current;
        prevReadyLayersSigRef.current = readyLayersSig;
        if (prev === readyLayersSig || !projectId || paths.size === 0)
            return;
        const prevSet = new Set(prev ? prev.split(',') : []);
        const grew = readyLayersSig.split(',').filter(Boolean).some(l => !prevSet.has(l));
        if (!grew)
            return;
        let cancelled = false;
        (async () => {
            var _a;
            for (const path of Array.from(paths.values())) {
                const gen = ++elevationFetchGenRef.current;
                const profiles = [];
                for (const seg of path.segments) {
                    const startPt = path.points.find(p => p.id === seg.startPointId);
                    const endPt = path.points.find(p => p.id === seg.endPointId);
                    if (!startPt || !endPt) {
                        profiles.push((_a = seg.elevationProfile) !== null && _a !== void 0 ? _a : null);
                        continue;
                    }
                    profiles.push(await fetchSegmentElevation(projectId, startPt.position, endPt.position, readyLayers));
                }
                if (cancelled || gen !== elevationFetchGenRef.current)
                    continue;
                setPaths(current => {
                    const newMap = new Map(current);
                    const existing = newMap.get(path.id);
                    if (!existing)
                        return current;
                    const updatedSegments = existing.segments.map((s, i) => { var _a; return ({ ...s, elevationProfile: (_a = profiles[i]) !== null && _a !== void 0 ? _a : s.elevationProfile }); });
                    newMap.set(path.id, { ...existing, segments: updatedSegments, totalElevationProfile: combinedElevationProfiles(updatedSegments) });
                    return newMap;
                });
            }
        })();
        return () => { cancelled = true; };
    }, [readyLayersSig, projectId, paths, readyLayers]);
    // Check if clicking on an existing path (can be called regardless of isActive)
    const checkExistingPathClick = useCallback((event) => {
        if (!viewer || viewer.isDestroyed())
            return false;
        const pickedObject = viewer.scene.pick(event.position);
        if (pickedObject === null || pickedObject === void 0 ? void 0 : pickedObject.id) {
            const entity = pickedObject.id;
            for (const [id, path] of paths.entries()) {
                for (const point of path.points) {
                    if (entity === point.entity) {
                        // A node click is an EDIT gesture, not an info gesture: the
                        // activation toggle already happened in handleMapMouseUp's
                        // no-move branch. Don't open, re-anchor, or un-hide the dialog
                        // (clicking the LINE below is the "bring the dialog back"
                        // gesture) — just consume the click so nothing else handles it.
                        return true;
                    }
                }
                for (let i = 0; i < path.segments.length; i++) {
                    const segment = path.segments[i];
                    if (entity === segment.lineEntity || entity === segment.labelEntity) {
                        setPathDialogHidden(false);
                        setSelectedPath({
                            pathId: id,
                            segmentIndex: i,
                            screenPosition: { x: event.position.x, y: event.position.y }
                        });
                        return true;
                    }
                }
            }
        }
        return false;
    }, [viewer, paths]);
    const addPointToPath = useCallback((pathId, position, atEnd) => {
        const path = paths.get(pathId);
        if (!path || !dataSourceRef.current)
            return null;
        pushUndoSnapshot();
        const pointId = generatePointId();
        const segmentId = generateSegmentId();
        if (atEnd) {
            // Add to end
            const lastPoint = path.points[path.points.length - 1];
            const distance = Cesium.Cartesian3.distance(lastPoint.position, position);
            // Update the current last point to be a middle point
            if (lastPoint.entity && dataSourceRef.current) {
                dataSourceRef.current.entities.remove(lastPoint.entity);
                const newMiddleEntity = createPointEntity(lastPoint.position, path.color, false, false);
                lastPoint.entity = newMiddleEntity || undefined;
            }
            // Create the new end point
            const pointEntity = createPointEntity(position, path.color, false, true);
            const lineEntity = createLineEntity(lastPoint.position, position, path.color, path.lineWidth);
            const labelEntity = createLabelEntity(lastPoint.position, position, distance, path.displayUnit) || undefined;
            const newPoint = {
                id: pointId,
                position,
                entity: pointEntity || undefined
            };
            const newSegment = {
                id: segmentId,
                startPointId: lastPoint.id,
                endPointId: pointId,
                distanceMeters: distance,
                lineEntity: lineEntity || undefined,
                labelEntity
            };
            setPaths(prev => {
                const newMap = new Map(prev);
                const existingPath = newMap.get(pathId);
                if (existingPath) {
                    newMap.set(pathId, {
                        ...existingPath,
                        points: [...existingPath.points, newPoint],
                        segments: [...existingPath.segments, newSegment],
                        totalDistanceMeters: existingPath.totalDistanceMeters + distance,
                        totalElevationProfile: undefined
                    });
                }
                return newMap;
            });
            // Fetch elevation profile for the new segment
            if (projectId) {
                const gen = ++elevationFetchGenRef.current;
                fetchSegmentElevationProfile(lastPoint.position, position, gen)
                    .then(profile => {
                    setPaths(prev => {
                        const newMap = new Map(prev);
                        const existingPath = newMap.get(pathId);
                        if (existingPath) {
                            const updatedSegments = [...existingPath.segments];
                            const newSegmentIndex = updatedSegments.length - 1;
                            updatedSegments[newSegmentIndex] = { ...updatedSegments[newSegmentIndex], elevationProfile: profile || null };
                            const totalProfile = combinedElevationProfiles(updatedSegments);
                            newMap.set(pathId, {
                                ...existingPath,
                                segments: updatedSegments,
                                totalElevationProfile: totalProfile
                            });
                        }
                        return newMap;
                    });
                });
            }
        }
        else {
            // Add to beginning
            const firstPoint = path.points[0];
            const distance = Cesium.Cartesian3.distance(position, firstPoint.position);
            // Update the current first point to be a middle point
            if (firstPoint.entity && dataSourceRef.current) {
                dataSourceRef.current.entities.remove(firstPoint.entity);
                const newMiddleEntity = createPointEntity(firstPoint.position, path.color, false, false);
                firstPoint.entity = newMiddleEntity || undefined;
            }
            // Create the new start point
            const pointEntity = createPointEntity(position, path.color, true, false);
            const lineEntity = createLineEntity(position, firstPoint.position, path.color, path.lineWidth);
            const labelEntity = createLabelEntity(position, firstPoint.position, distance, path.displayUnit) || undefined;
            const newPoint = {
                id: pointId,
                position,
                entity: pointEntity || undefined
            };
            const newSegment = {
                id: segmentId,
                startPointId: pointId,
                endPointId: firstPoint.id,
                distanceMeters: distance,
                lineEntity: lineEntity || undefined,
                labelEntity
            };
            setPaths(prev => {
                const newMap = new Map(prev);
                const existingPath = newMap.get(pathId);
                if (existingPath) {
                    newMap.set(pathId, {
                        ...existingPath,
                        points: [newPoint, ...existingPath.points],
                        segments: [newSegment, ...existingPath.segments],
                        totalDistanceMeters: existingPath.totalDistanceMeters + distance,
                        totalElevationProfile: undefined
                    });
                }
                return newMap;
            });
            // Fetch elevation profile for the new segment
            if (projectId) {
                const gen = ++elevationFetchGenRef.current;
                fetchSegmentElevationProfile(position, firstPoint.position, gen)
                    .then(profile => {
                    setPaths(prev => {
                        const newMap = new Map(prev);
                        const existingPath = newMap.get(pathId);
                        if (existingPath) {
                            const updatedSegments = [...existingPath.segments];
                            updatedSegments[0] = { ...updatedSegments[0], elevationProfile: profile || null };
                            const totalProfile = combinedElevationProfiles(updatedSegments);
                            newMap.set(pathId, {
                                ...existingPath,
                                segments: updatedSegments,
                                totalElevationProfile: totalProfile
                            });
                        }
                        return newMap;
                    });
                });
            }
        }
        return pointId;
    }, [paths, createPointEntity, createLineEntity, createLabelEntity, showSegmentLabels, projectId, fetchSegmentElevationProfile, combinedElevationProfiles, pushUndoSnapshot]);
    const insertNodeOnSegment = useCallback((pathId, segmentIndex, position) => {
        const path = paths.get(pathId);
        if (!path || !dataSourceRef.current)
            return null;
        const segment = path.segments[segmentIndex];
        if (!segment)
            return null;
        const startPointIndex = path.points.findIndex(p => p.id === segment.startPointId);
        const endPointIndex = path.points.findIndex(p => p.id === segment.endPointId);
        if (startPointIndex === -1 || endPointIndex === -1)
            return null;
        pushUndoSnapshot();
        const startPoint = path.points[startPointIndex];
        const endPoint = path.points[endPointIndex];
        // Remove old segment entities
        if (segment.lineEntity)
            dataSourceRef.current.entities.remove(segment.lineEntity);
        if (segment.labelEntity)
            dataSourceRef.current.entities.remove(segment.labelEntity);
        // Create new point
        const pointId = generatePointId();
        const pointEntity = createPointEntity(position, path.color, false, false);
        const newPoint = {
            id: pointId,
            position,
            entity: pointEntity || undefined
        };
        // Create two new segments
        const dist1 = Cesium.Cartesian3.distance(startPoint.position, position);
        const dist2 = Cesium.Cartesian3.distance(position, endPoint.position);
        const line1 = createLineEntity(startPoint.position, position, path.color, path.lineWidth);
        const line2 = createLineEntity(position, endPoint.position, path.color, path.lineWidth);
        const label1 = createLabelEntity(startPoint.position, position, dist1, path.displayUnit) || undefined;
        const label2 = createLabelEntity(position, endPoint.position, dist2, path.displayUnit) || undefined;
        const seg1 = {
            id: generateSegmentId(),
            startPointId: startPoint.id,
            endPointId: pointId,
            distanceMeters: dist1,
            lineEntity: line1 || undefined,
            labelEntity: label1
        };
        const seg2 = {
            id: generateSegmentId(),
            startPointId: pointId,
            endPointId: endPoint.id,
            distanceMeters: dist2,
            lineEntity: line2 || undefined,
            labelEntity: label2
        };
        // Insert point between start and end
        const newPoints = [...path.points];
        newPoints.splice(endPointIndex, 0, newPoint);
        // Replace old segment with two new ones
        const newSegments = [...path.segments];
        newSegments.splice(segmentIndex, 1, seg1, seg2);
        const totalDistance = newSegments.reduce((sum, s) => sum + s.distanceMeters, 0);
        setPaths(prev => {
            const newMap = new Map(prev);
            newMap.set(pathId, {
                ...path,
                points: newPoints,
                segments: newSegments,
                totalDistanceMeters: totalDistance,
                totalElevationProfile: undefined
            });
            return newMap;
        });
        // Fetch elevation profiles for the two new segments
        if (projectId) {
            const gen = ++elevationFetchGenRef.current;
            fetchSegmentElevationProfile(startPoint.position, position, gen).then(profile => {
                setPaths(prev => {
                    const newMap = new Map(prev);
                    const existingPath = newMap.get(pathId);
                    if (existingPath) {
                        const updatedSegments = [...existingPath.segments];
                        const idx = updatedSegments.findIndex(s => s.id === seg1.id);
                        if (idx !== -1) {
                            updatedSegments[idx] = { ...updatedSegments[idx], elevationProfile: profile || null };
                            const totalProfile = combinedElevationProfiles(updatedSegments);
                            newMap.set(pathId, { ...existingPath, segments: updatedSegments, totalElevationProfile: totalProfile });
                        }
                    }
                    return newMap;
                });
            });
            fetchSegmentElevationProfile(position, endPoint.position, gen).then(profile => {
                setPaths(prev => {
                    const newMap = new Map(prev);
                    const existingPath = newMap.get(pathId);
                    if (existingPath) {
                        const updatedSegments = [...existingPath.segments];
                        const idx = updatedSegments.findIndex(s => s.id === seg2.id);
                        if (idx !== -1) {
                            updatedSegments[idx] = { ...updatedSegments[idx], elevationProfile: profile || null };
                            const totalProfile = combinedElevationProfiles(updatedSegments);
                            newMap.set(pathId, { ...existingPath, segments: updatedSegments, totalElevationProfile: totalProfile });
                        }
                    }
                    return newMap;
                });
            });
        }
        return pointId;
    }, [paths, createPointEntity, createLineEntity, createLabelEntity, showSegmentLabels, projectId, fetchSegmentElevationProfile, combinedElevationProfiles, pushUndoSnapshot]);
    const insertNodeOnPendingSegment = useCallback((segmentIndex, position) => {
        if (!pendingPath || !dataSourceRef.current)
            return null;
        const segment = pendingPath.segments[segmentIndex];
        if (!segment)
            return null;
        const startPointIndex = pendingPath.points.findIndex(p => p.id === segment.startPointId);
        const endPointIndex = pendingPath.points.findIndex(p => p.id === segment.endPointId);
        if (startPointIndex === -1 || endPointIndex === -1)
            return null;
        const startPoint = pendingPath.points[startPointIndex];
        const endPoint = pendingPath.points[endPointIndex];
        if (segment.lineEntity)
            dataSourceRef.current.entities.remove(segment.lineEntity);
        if (segment.labelEntity)
            dataSourceRef.current.entities.remove(segment.labelEntity);
        const pointId = generatePointId();
        const pointEntity = createPointEntity(position, DEFAULT_PATH_COLOR, false, false);
        const newPoint = {
            id: pointId,
            position,
            entity: pointEntity || undefined
        };
        const dist1 = Cesium.Cartesian3.distance(startPoint.position, position);
        const dist2 = Cesium.Cartesian3.distance(position, endPoint.position);
        const line1 = createLineEntity(startPoint.position, position, DEFAULT_PATH_COLOR);
        const line2 = createLineEntity(position, endPoint.position, DEFAULT_PATH_COLOR);
        const label1 = createLabelEntity(startPoint.position, position, dist1, defaultUnit) || undefined;
        const label2 = createLabelEntity(position, endPoint.position, dist2, defaultUnit) || undefined;
        const seg1 = {
            id: generateSegmentId(),
            startPointId: startPoint.id,
            endPointId: pointId,
            distanceMeters: dist1,
            lineEntity: line1 || undefined,
            labelEntity: label1
        };
        const seg2 = {
            id: generateSegmentId(),
            startPointId: pointId,
            endPointId: endPoint.id,
            distanceMeters: dist2,
            lineEntity: line2 || undefined,
            labelEntity: label2
        };
        setPendingPath(prev => {
            if (!prev)
                return null;
            const newPoints = [...prev.points];
            const insertAt = prev.points.findIndex(p => p.id === endPoint.id);
            if (insertAt === -1)
                return prev;
            newPoints.splice(insertAt, 0, newPoint);
            const newSegments = [...prev.segments];
            const segIdx = prev.segments.findIndex(s => s.id === segment.id);
            if (segIdx === -1)
                return prev;
            newSegments.splice(segIdx, 1, seg1, seg2);
            return { points: newPoints, segments: newSegments };
        });
        return pointId;
    }, [pendingPath, createPointEntity, createLineEntity, createLabelEntity, showSegmentLabels, defaultUnit]);
    // Sync function refs for handler stability
    useEffect(() => { pickTerrainPositionRef.current = pickTerrainPosition; }, [pickTerrainPosition]);
    useEffect(() => { isPositionInConstraintRef.current = isPositionInConstraint; }, [isPositionInConstraint]);
    useEffect(() => { createPointEntityRef.current = createPointEntity; }, [createPointEntity]);
    useEffect(() => { createLineEntityRef.current = createLineEntity; }, [createLineEntity]);
    useEffect(() => { createLabelEntityRef.current = createLabelEntity; }, [createLabelEntity]);
    useEffect(() => { addPointToPathRef.current = addPointToPath; }, [addPointToPath]);
    useEffect(() => { checkExistingPathClickRef.current = checkExistingPathClick; }, [checkExistingPathClick]);
    useEffect(() => { insertNodeOnSegmentRef.current = insertNodeOnSegment; }, [insertNodeOnSegment]);
    useEffect(() => { insertNodeOnPendingSegmentRef.current = insertNodeOnPendingSegment; }, [insertNodeOnPendingSegment]);
    // deleteNodeRef and cancelPendingRef are synced after their definitions, below.
    const handleMapClick = useCallback((event) => {
        if (!isActive || !viewer || viewer.isDestroyed())
            return false;
        if (draggingRef.current || draggingPendingRef.current)
            return false;
        // Phantom-node click: if the user clicks while the insert-here ghost is
        // visible, treat it as a click on the underlying segment and insert a
        // node at the phantom's pinned position. The new node becomes the
        // active drawing vertex; the next click in empty space still extends
        // from the terminal (existing behaviour).
        const pickedForPhantom = viewer.scene.pick(event.position);
        if ((pickedForPhantom === null || pickedForPhantom === void 0 ? void 0 : pickedForPhantom.id) &&
            pickedForPhantom.id === phantomNodeRef.current &&
            phantomTargetRef.current) {
            const target = phantomTargetRef.current;
            const newPointId = insertNodeOnSegmentRef.current(target.pathId, target.segmentIndex, target.position);
            hidePhantom();
            if (newPointId) {
                setSelectedDrawingVertex({ kind: 'completed', pathId: target.pathId, pointId: newPointId });
            }
            return true;
        }
        // Clear any drawing-vertex selection on empty-terrain click. The new point will still be added
        // by the existing branch below (per user request: deselect AND add the point in one click).
        if (selectedDrawingVertexRef.current) {
            setSelectedDrawingVertex(null);
        }
        // Edit mode: scope all clicks to the path being edited.
        //   - Clicking a segment line → insert a node on that segment.
        //   - Clicking empty terrain → extend the editing path (anchor-or-nearest-end).
        //   - Clicks elsewhere are consumed so other paths' click handlers don't fire.
        const editingId = editingPathIdRef.current;
        if (editingId) {
            const pickedObject = viewer.scene.pick(event.position);
            if (pickedObject === null || pickedObject === void 0 ? void 0 : pickedObject.id) {
                const entity = pickedObject.id;
                const path = pathsRef.current.get(editingId);
                if (path) {
                    for (let i = 0; i < path.segments.length; i++) {
                        if (entity === path.segments[i].lineEntity) {
                            const clickPos = pickTerrainPositionRef.current(event.position);
                            if (clickPos) {
                                insertNodeOnSegmentRef.current(editingId, i, clickPos);
                            }
                            return true;
                        }
                    }
                }
            }
            const path = pathsRef.current.get(editingId);
            if (path && path.points.length > 0) {
                const position = pickTerrainPositionRef.current(event.position);
                if (position && isPositionInConstraintRef.current(position)) {
                    // Middle-node-selected → insert between selected and next-in-sequence.
                    // Endpoints fall through to the extend logic below.
                    const drawSel = selectedDrawingVertexRef.current;
                    if ((drawSel === null || drawSel === void 0 ? void 0 : drawSel.kind) === 'completed' && drawSel.pathId === editingId) {
                        const idx = path.points.findIndex(p => p.id === drawSel.pointId);
                        if (idx > 0 && idx < path.points.length - 1) {
                            const newPointId = insertNodeOnSegmentRef.current(editingId, idx, position);
                            if (newPointId) {
                                setSelectedDrawingVertex({ kind: 'completed', pathId: editingId, pointId: newPointId });
                            }
                            return true;
                        }
                    }
                    const anchor = extendAnchorEndRef.current;
                    let chosenEnd;
                    if (anchor) {
                        chosenEnd = anchor;
                    }
                    else {
                        const firstPoint = path.points[0];
                        const lastPoint = path.points[path.points.length - 1];
                        const distToFirst = Cesium.Cartesian3.distance(position, firstPoint.position);
                        const distToLast = Cesium.Cartesian3.distance(position, lastPoint.position);
                        chosenEnd = distToLast <= distToFirst ? 'last' : 'first';
                    }
                    const newPointId = addPointToPathRef.current(editingId, position, chosenEnd === 'last');
                    setExtendAnchorEnd(chosenEnd);
                    const prevSelected = selectedDrawingVertexRef.current;
                    if (newPointId && (prevSelected === null || prevSelected === void 0 ? void 0 : prevSelected.kind) === 'completed' && prevSelected.pathId === editingId) {
                        setSelectedDrawingVertex({ kind: 'completed', pathId: editingId, pointId: newPointId });
                    }
                }
            }
            return true;
        }
        // Handle extend mode - add points to existing path or insert node on segment
        const extending = extendingPathRef.current;
        if (extending) {
            // Check if clicking on a segment line — insert a node on that segment
            const pickedObject = viewer.scene.pick(event.position);
            if (pickedObject === null || pickedObject === void 0 ? void 0 : pickedObject.id) {
                const entity = pickedObject.id;
                const path = pathsRef.current.get(extending.pathId);
                if (path) {
                    for (let i = 0; i < path.segments.length; i++) {
                        if (entity === path.segments[i].lineEntity) {
                            const clickPos = pickTerrainPositionRef.current(event.position);
                            if (clickPos) {
                                insertNodeOnSegmentRef.current(extending.pathId, i, clickPos);
                            }
                            return true;
                        }
                    }
                }
            }
            const position = pickTerrainPositionRef.current(event.position);
            if (!position)
                return false;
            if (!isPositionInConstraintRef.current(position))
                return false;
            const path = pathsRef.current.get(extending.pathId);
            if (!path)
                return false;
            const firstPoint = path.points[0];
            const lastPoint = path.points[path.points.length - 1];
            const distToFirst = Cesium.Cartesian3.distance(position, firstPoint.position);
            const distToLast = Cesium.Cartesian3.distance(position, lastPoint.position);
            const fromEnd = distToLast <= distToFirst;
            addPointToPathRef.current(extending.pathId, position, fromEnd);
            return true;
        }
        // Click on an existing path's points / segments / labels → open the
        // path's options dialog. Node insertion on segment clicks happens
        // ONLY in edit mode (handled in the editingId block above) or
        // extend mode (handled in the extendingPath block above) — clicking
        // a path's line outside those modes used to insert a node into any
        // path the user happened to click, which surprised users with
        // unsaved paths still on the map.
        if (checkExistingPathClickRef.current(event)) {
            return true;
        }
        const position = pickTerrainPositionRef.current(event.position);
        if (!position)
            return false;
        if (!isPositionInConstraintRef.current(position))
            return false;
        const pending = pendingPathRef.current;
        // Edit-mode extend: if a path is selected (its dialog is open) and we're not
        // already drawing a new path or in explicit extending mode, route this click
        // to extend the selected path. Keeps the dialog open so metadata edits stay live.
        //
        // End-selection logic combines (1) endpoint-as-anchor and (3) sticky-last-end:
        //   - If extendAnchorEnd is set (user clicked an endpoint, or a previous extend
        //     locked an end), use it.
        //   - Otherwise fall back to the nearest-end heuristic for the first click,
        //     and persist that decision so subsequent clicks stay on the same end.
        const sel = selectedPathRef.current;
        if (!pending && sel && pathsRef.current.has(sel.pathId)) {
            const path = pathsRef.current.get(sel.pathId);
            if (path.points.length > 0) {
                // Middle-node-selected → insert between selected and next-in-sequence.
                // Endpoints fall through to the extend logic below.
                const drawSel = selectedDrawingVertexRef.current;
                if ((drawSel === null || drawSel === void 0 ? void 0 : drawSel.kind) === 'completed' && drawSel.pathId === sel.pathId) {
                    const idx = path.points.findIndex(p => p.id === drawSel.pointId);
                    if (idx > 0 && idx < path.points.length - 1) {
                        const newPointId = insertNodeOnSegmentRef.current(sel.pathId, idx, position);
                        if (newPointId) {
                            setSelectedDrawingVertex({ kind: 'completed', pathId: sel.pathId, pointId: newPointId });
                        }
                        return true;
                    }
                }
                const anchor = extendAnchorEndRef.current;
                let chosenEnd;
                if (anchor) {
                    chosenEnd = anchor;
                }
                else {
                    const firstPoint = path.points[0];
                    const lastPoint = path.points[path.points.length - 1];
                    const distToFirst = Cesium.Cartesian3.distance(position, firstPoint.position);
                    const distToLast = Cesium.Cartesian3.distance(position, lastPoint.position);
                    chosenEnd = distToLast <= distToFirst ? 'last' : 'first';
                }
                const newPointId = addPointToPathRef.current(sel.pathId, position, chosenEnd === 'last');
                // Sticky: lock subsequent clicks to the same end.
                setExtendAnchorEnd(chosenEnd);
                // If the user had explicitly highlighted the endpoint (yellow), move the
                // highlight to the new endpoint so the active end stays visible.
                const prevSelected = selectedDrawingVertexRef.current;
                if (newPointId && (prevSelected === null || prevSelected === void 0 ? void 0 : prevSelected.kind) === 'completed' && prevSelected.pathId === sel.pathId) {
                    setSelectedDrawingVertex({ kind: 'completed', pathId: sel.pathId, pointId: newPointId });
                }
                return true;
            }
        }
        if (!pending) {
            const pointId = generatePointId();
            const pointEntity = createPointEntityRef.current(position, DEFAULT_PATH_COLOR, true, false);
            if (pointEntity) {
                const newPoint = {
                    id: pointId,
                    position,
                    entity: pointEntity
                };
                setSelectedPath(null);
                setPendingPath({
                    points: [newPoint],
                    segments: []
                });
                // Highlight the just-placed node as the active end (yellow).
                setSelectedDrawingVertex({ kind: 'pending', pointId });
            }
            return true;
        }
        // Pending-segment click → insert a node on that segment at the click position.
        const pendingPicked = viewer.scene.pick(event.position);
        if (pendingPicked === null || pendingPicked === void 0 ? void 0 : pendingPicked.id) {
            const entity = pendingPicked.id;
            for (let i = 0; i < pending.segments.length; i++) {
                if (entity === pending.segments[i].lineEntity) {
                    const newPointId = insertNodeOnPendingSegmentRef.current(i, position);
                    if (newPointId) {
                        setSelectedDrawingVertex({ kind: 'pending', pointId: newPointId });
                    }
                    return true;
                }
            }
        }
        // Middle-node-selected → insert between selected and next-in-sequence.
        // Endpoints fall through to the extend-from-end logic below.
        const drawSel = selectedDrawingVertexRef.current;
        if ((drawSel === null || drawSel === void 0 ? void 0 : drawSel.kind) === 'pending') {
            const idx = pending.points.findIndex(p => p.id === drawSel.pointId);
            if (idx > 0 && idx < pending.points.length - 1) {
                const newPointId = insertNodeOnPendingSegmentRef.current(idx, position);
                if (newPointId) {
                    setSelectedDrawingVertex({ kind: 'pending', pointId: newPointId });
                }
                return true;
            }
        }
        const lastPoint = pending.points[pending.points.length - 1];
        const pointId = generatePointId();
        const segmentId = generateSegmentId();
        const distance = Cesium.Cartesian3.distance(lastPoint.position, position);
        const lineEntity = createLineEntityRef.current(lastPoint.position, position, DEFAULT_PATH_COLOR);
        const labelEntity = createLabelEntityRef.current(lastPoint.position, position, distance, defaultUnitRef.current) || undefined;
        const pointEntity = createPointEntityRef.current(position, DEFAULT_PATH_COLOR, false, false);
        const newSegment = {
            id: segmentId,
            startPointId: lastPoint.id,
            endPointId: pointId,
            distanceMeters: distance,
            lineEntity: lineEntity || undefined,
            labelEntity
        };
        const newPoint = {
            id: pointId,
            position,
            entity: pointEntity || undefined
        };
        setPendingPath(prev => {
            if (!prev)
                return null;
            return {
                points: [...prev.points, newPoint],
                segments: [...prev.segments, newSegment]
            };
        });
        // Highlight the just-placed node as the active end (yellow).
        setSelectedDrawingVertex({ kind: 'pending', pointId });
        return true;
    }, [isActive, viewer, hidePhantom]);
    const handleMapDoubleClick = useCallback((event) => {
        const pending = pendingPathRef.current;
        if (!isActive || !pending || pending.points.length < 2)
            return false;
        finishPathRef.current();
        return true;
    }, [isActive]);
    const finishPath = useCallback((name, bufferConfig) => {
        if (!pendingPath || pendingPath.points.length < 2)
            return;
        pushUndoSnapshot();
        const pathId = generatePathId();
        const pathName = name || nextDefaultPathName(paths);
        const lastPoint = pendingPath.points[pendingPath.points.length - 1];
        if (lastPoint.entity && dataSourceRef.current) {
            dataSourceRef.current.entities.remove(lastPoint.entity);
            const newLastPointEntity = createPointEntity(lastPoint.position, DEFAULT_PATH_COLOR, false, true);
            lastPoint.entity = newLastPointEntity || undefined;
        }
        const totalDistance = pendingPath.segments.reduce((sum, s) => sum + s.distanceMeters, 0);
        const newPath = {
            id: pathId,
            name: pathName,
            points: pendingPath.points,
            segments: pendingPath.segments,
            totalDistanceMeters: totalDistance,
            color: DEFAULT_PATH_COLOR,
            displayUnit: defaultUnit,
            projectId: projectId || undefined,
            // Capture the draw-HUD toggle: the finished path remembers whether its
            // segments were showing, and the dialog toggle takes over from here.
            showMeasurements: showSegmentLabelsRef.current
        };
        setPaths(prev => {
            const newMap = new Map(prev);
            newMap.set(pathId, newPath);
            return newMap;
        });
        if (projectId) {
            const gen = ++elevationFetchGenRef.current;
            pendingPath.segments.forEach((segment, index) => {
                const startPoint = pendingPath.points.find(p => p.id === segment.startPointId);
                const endPoint = pendingPath.points.find(p => p.id === segment.endPointId);
                if (startPoint && endPoint) {
                    fetchSegmentElevationProfile(startPoint.position, endPoint.position, gen)
                        .then(profile => {
                        setPaths(prev => {
                            const newMap = new Map(prev);
                            const existingPath = newMap.get(pathId);
                            if (existingPath) {
                                const updatedSegments = [...existingPath.segments];
                                updatedSegments[index] = { ...updatedSegments[index], elevationProfile: profile || null };
                                const totalProfile = combinedElevationProfiles(updatedSegments);
                                newMap.set(pathId, {
                                    ...existingPath,
                                    segments: updatedSegments,
                                    totalElevationProfile: totalProfile
                                });
                            }
                            return newMap;
                        });
                    });
                }
            });
        }
        // Calculate buffer if config provided
        if (bufferConfig && bufferConfig.distance > 0 && projectId) {
            const bufferDistanceMeters = bufferConfig.unit === 'ft'
                ? bufferConfig.distance * 0.3048
                : bufferConfig.distance;
            const coords = pendingPath.points.map(pt => {
                const cart = Cesium.Cartographic.fromCartesian(pt.position);
                return {
                    lat: Cesium.Math.toDegrees(cart.latitude),
                    lng: Cesium.Math.toDegrees(cart.longitude)
                };
            });
            elevationService.calculateStreamBuffer(projectId, coords, bufferDistanceMeters)
                .then(result => {
                if (result.success && result.bufferPolygons.length > 0 && dataSourceRef.current) {
                    const pathColor = Cesium.Color.fromCssColorString(DEFAULT_PATH_COLOR);
                    const fillMaterial = new Cesium.ColorMaterialProperty(pathColor.withAlpha(0.3));
                    const outlineColorProp = new Cesium.ConstantProperty(pathColor.withAlpha(0.8));
                    const bufferEntities = result.bufferPolygons.map(poly => dataSourceRef.current.entities.add({
                        polygon: {
                            hierarchy: buildBufferHierarchy(poly),
                            material: fillMaterial,
                            outline: true,
                            outlineColor: outlineColorProp,
                            outlineWidth: 2,
                            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND
                        }
                    }));
                    const bufferAreaSqMeters = calculateBufferPolygonsAreaM2(result.bufferPolygons);
                    setPaths(prev => {
                        const newMap = new Map(prev);
                        const existingPath = newMap.get(pathId);
                        if (existingPath) {
                            newMap.set(pathId, {
                                ...existingPath,
                                bufferEntities,
                                bufferDistanceMeters,
                                bufferUnit: bufferConfig.unit,
                                bufferAreaSqMeters,
                                bufferPolygons: result.bufferPolygons
                            });
                        }
                        return newMap;
                    });
                }
            })
                .catch(err => {
                console.error('Failed to calculate path buffer:', err);
            });
        }
        setPendingPath(null);
        setSelectedDrawingVertex(null);
        setExtendAnchorEnd(null);
        setPathDialogHidden(false);
        setSelectedPath({
            pathId,
            screenPosition: { x: window.innerWidth / 2, y: window.innerHeight / 2 }
        });
        return pathId;
    }, [pendingPath, paths, defaultUnit, projectId, createPointEntity, fetchSegmentElevationProfile, combinedElevationProfiles, pushUndoSnapshot]);
    useEffect(() => { finishPathRef.current = finishPath; }, [finishPath]);
    const applyBuffer = useCallback((pathId, bufferConfig) => {
        if (!projectId || !dataSourceRef.current)
            return;
        const path = paths.get(pathId);
        if (!path)
            return;
        const bufferDistanceMeters = bufferConfig.unit === 'ft'
            ? bufferConfig.distance * 0.3048
            : bufferConfig.distance;
        const coords = path.points.map(pt => {
            const cart = Cesium.Cartographic.fromCartesian(pt.position);
            return {
                lat: Cesium.Math.toDegrees(cart.latitude),
                lng: Cesium.Math.toDegrees(cart.longitude)
            };
        });
        // Remove old buffer entities before calculating new ones
        if (path.bufferEntities && dataSourceRef.current) {
            path.bufferEntities.forEach(e => dataSourceRef.current.entities.remove(e));
        }
        elevationService.calculateStreamBuffer(projectId, coords, bufferDistanceMeters)
            .then(result => {
            if (result.success && result.bufferPolygons.length > 0 && dataSourceRef.current) {
                const pathColor = Cesium.Color.fromCssColorString(path.color || DEFAULT_PATH_COLOR);
                const fillMaterial = new Cesium.ColorMaterialProperty(pathColor.withAlpha(0.3));
                const outlineColorProp = new Cesium.ConstantProperty(pathColor.withAlpha(0.8));
                const bufferEntities = result.bufferPolygons.map(poly => dataSourceRef.current.entities.add({
                    polygon: {
                        hierarchy: buildBufferHierarchy(poly),
                        material: fillMaterial,
                        outline: true,
                        outlineColor: outlineColorProp,
                        outlineWidth: 2,
                        heightReference: Cesium.HeightReference.CLAMP_TO_GROUND
                    }
                }));
                const bufferAreaSqMeters = calculateBufferPolygonsAreaM2(result.bufferPolygons);
                setPaths(prev => {
                    const newMap = new Map(prev);
                    const existingPath = newMap.get(pathId);
                    if (existingPath) {
                        newMap.set(pathId, {
                            ...existingPath,
                            bufferEntities,
                            bufferDistanceMeters,
                            bufferUnit: bufferConfig.unit,
                            bufferAreaSqMeters,
                            bufferPolygons: result.bufferPolygons
                        });
                    }
                    return newMap;
                });
            }
        })
            .catch(err => {
            console.error('Failed to calculate path buffer:', err);
        });
    }, [paths, projectId]);
    const removeBuffer = useCallback((pathId) => {
        const path = paths.get(pathId);
        if (!path)
            return;
        if (path.bufferEntities && dataSourceRef.current) {
            path.bufferEntities.forEach(e => dataSourceRef.current.entities.remove(e));
        }
        setPaths(prev => {
            const newMap = new Map(prev);
            const existingPath = newMap.get(pathId);
            if (existingPath) {
                newMap.set(pathId, {
                    ...existingPath,
                    bufferEntities: undefined,
                    bufferDistanceMeters: undefined,
                    bufferUnit: undefined,
                    bufferAreaSqMeters: undefined,
                    bufferPolygons: undefined
                });
            }
            return newMap;
        });
    }, [paths]);
    // Rehydrate a buffer onto an existing local path from previously-persisted
    // polygons. Used when opening a saved path artifact whose geometry carries
    // buffer config + polygons — skips the calculateStreamBuffer roundtrip the
    // live tool would otherwise make. Mirrors the entity-creation block in
    // applyBuffer so view/edit-mode visuals match the live tool exactly.
    const restoreBuffer = useCallback((pathId, buffer) => {
        var _a;
        if (!dataSourceRef.current)
            return;
        const path = paths.get(pathId);
        if (!path)
            return;
        if (!buffer.polygons || buffer.polygons.length === 0)
            return;
        if (path.bufferEntities) {
            path.bufferEntities.forEach(e => dataSourceRef.current.entities.remove(e));
        }
        const pathColor = Cesium.Color.fromCssColorString(path.color || DEFAULT_PATH_COLOR);
        const fillMaterial = new Cesium.ColorMaterialProperty(pathColor.withAlpha(0.3));
        const outlineColorProp = new Cesium.ConstantProperty(pathColor.withAlpha(0.8));
        const bufferEntities = buffer.polygons.map(poly => dataSourceRef.current.entities.add({
            polygon: {
                hierarchy: buildBufferHierarchy(poly),
                material: fillMaterial,
                outline: true,
                outlineColor: outlineColorProp,
                outlineWidth: 2,
                heightReference: Cesium.HeightReference.CLAMP_TO_GROUND
            }
        }));
        const areaSqMeters = (_a = buffer.areaSqMeters) !== null && _a !== void 0 ? _a : calculateBufferPolygonsAreaM2(buffer.polygons);
        setPaths(prev => {
            const newMap = new Map(prev);
            const existing = newMap.get(pathId);
            if (existing) {
                newMap.set(pathId, {
                    ...existing,
                    bufferEntities,
                    bufferDistanceMeters: buffer.distanceMeters,
                    bufferUnit: buffer.unit,
                    bufferAreaSqMeters: areaSqMeters,
                    bufferPolygons: buffer.polygons
                });
            }
            return newMap;
        });
    }, [paths]);
    const updateDraggedPendingPoint = useCallback((newPosition) => {
        var _a, _b;
        if (!draggingPending || !pendingPath)
            return;
        const pointIndex = draggingPending.pointIndex;
        const point = pendingPath.points[pointIndex];
        if (point.entity) {
            point.entity.position = new Cesium.ConstantPositionProperty(newPosition);
        }
        if (pointIndex > 0) {
            const prevSegment = pendingPath.segments[pointIndex - 1];
            if ((_a = prevSegment === null || prevSegment === void 0 ? void 0 : prevSegment.labelEntity) === null || _a === void 0 ? void 0 : _a.label) {
                const prevPoint = pendingPath.points[pointIndex - 1];
                const midpoint = Cesium.Cartesian3.midpoint(prevPoint.position, newPosition, new Cesium.Cartesian3());
                prevSegment.labelEntity.position = midpoint;
                const newDistance = Cesium.Cartesian3.distance(prevPoint.position, newPosition);
                prevSegment.labelEntity.label.text = new Cesium.ConstantProperty(formatDistance(newDistance, defaultUnit));
            }
        }
        if (pointIndex < pendingPath.segments.length) {
            const nextSegment = pendingPath.segments[pointIndex];
            if ((_b = nextSegment === null || nextSegment === void 0 ? void 0 : nextSegment.labelEntity) === null || _b === void 0 ? void 0 : _b.label) {
                const nextPoint = pendingPath.points[pointIndex + 1];
                const midpoint = Cesium.Cartesian3.midpoint(newPosition, nextPoint.position, new Cesium.Cartesian3());
                nextSegment.labelEntity.position = midpoint;
                const newDistance = Cesium.Cartesian3.distance(newPosition, nextPoint.position);
                nextSegment.labelEntity.label.text = new Cesium.ConstantProperty(formatDistance(newDistance, defaultUnit));
            }
        }
    }, [draggingPending, pendingPath, defaultUnit]);
    // While a pending-path point is being dragged, swap connected segments' polyline.positions
    // to a CallbackProperty so the line tracks the cursor live.
    useEffect(() => {
        if (!draggingPending)
            return;
        const pending = pendingPathRef.current;
        if (!pending)
            return;
        const pointIndex = draggingPending.pointIndex;
        const affected = [];
        if (pointIndex > 0) {
            const prevSegment = pending.segments[pointIndex - 1];
            if (prevSegment) {
                affected.push({
                    segment: prevSegment,
                    startPoint: pending.points[pointIndex - 1],
                    endPoint: pending.points[pointIndex]
                });
            }
        }
        if (pointIndex < pending.segments.length) {
            const nextSegment = pending.segments[pointIndex];
            if (nextSegment) {
                affected.push({
                    segment: nextSegment,
                    startPoint: pending.points[pointIndex],
                    endPoint: pending.points[pointIndex + 1]
                });
            }
        }
        const livePositions = (startPoint, endPoint) => {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            const now = Cesium.JulianDate.now();
            const startPos = (_d = (_c = (_b = (_a = startPoint.entity) === null || _a === void 0 ? void 0 : _a.position) === null || _b === void 0 ? void 0 : _b.getValue) === null || _c === void 0 ? void 0 : _c.call(_b, now)) !== null && _d !== void 0 ? _d : startPoint.position;
            const endPos = (_h = (_g = (_f = (_e = endPoint.entity) === null || _e === void 0 ? void 0 : _e.position) === null || _f === void 0 ? void 0 : _f.getValue) === null || _g === void 0 ? void 0 : _g.call(_f, now)) !== null && _h !== void 0 ? _h : endPoint.position;
            return [startPos, endPos];
        };
        affected.forEach(({ segment, startPoint, endPoint }) => {
            var _a;
            if (!((_a = segment.lineEntity) === null || _a === void 0 ? void 0 : _a.polyline))
                return;
            segment.lineEntity.polyline.positions = new Cesium.CallbackProperty(() => livePositions(startPoint, endPoint), false);
        });
        return () => {
            affected.forEach(({ segment, startPoint, endPoint }) => {
                var _a;
                if (!((_a = segment.lineEntity) === null || _a === void 0 ? void 0 : _a.polyline))
                    return;
                segment.lineEntity.polyline.positions = new Cesium.ConstantProperty(livePositions(startPoint, endPoint));
            });
        };
    }, [draggingPending]);
    const updateDraggedPoint = useCallback((newPosition) => {
        if (!dragging)
            return;
        const path = paths.get(dragging.pathId);
        if (!path)
            return;
        const pointIndex = path.points.findIndex(p => p.id === dragging.pointId);
        if (pointIndex === -1)
            return;
        const point = path.points[pointIndex];
        if (point.entity) {
            point.entity.position = new Cesium.ConstantPositionProperty(newPosition);
        }
        path.segments.forEach((segment) => {
            var _a;
            if (segment.startPointId !== dragging.pointId && segment.endPointId !== dragging.pointId)
                return;
            const startPoint = path.points.find(p => p.id === segment.startPointId);
            const endPoint = path.points.find(p => p.id === segment.endPointId);
            if (!startPoint || !endPoint)
                return;
            const startPos = startPoint.id === dragging.pointId ? newPosition : startPoint.position;
            const endPos = endPoint.id === dragging.pointId ? newPosition : endPoint.position;
            const newDistance = Cesium.Cartesian3.distance(startPos, endPos);
            if ((_a = segment.labelEntity) === null || _a === void 0 ? void 0 : _a.label) {
                const midpoint = Cesium.Cartesian3.midpoint(startPos, endPos, new Cesium.Cartesian3());
                segment.labelEntity.position = midpoint;
                segment.labelEntity.label.text = new Cesium.ConstantProperty(formatDistance(newDistance, path.displayUnit));
            }
        });
    }, [dragging, paths]);
    // While a completed-path point is being dragged, swap each connected segment's
    // polyline.positions to a CallbackProperty so the line tracks the cursor live
    // (clampToGround polylines need per-frame re-evaluation to follow smoothly).
    useEffect(() => {
        if (!dragging)
            return;
        const path = pathsRef.current.get(dragging.pathId);
        if (!path)
            return;
        const draggedPointId = dragging.pointId;
        const affected = [];
        path.segments.forEach(segment => {
            if (segment.startPointId !== draggedPointId && segment.endPointId !== draggedPointId)
                return;
            const startPoint = path.points.find(p => p.id === segment.startPointId);
            const endPoint = path.points.find(p => p.id === segment.endPointId);
            if (startPoint && endPoint)
                affected.push({ segment, startPoint, endPoint });
        });
        const livePositions = (startPoint, endPoint) => {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            const now = Cesium.JulianDate.now();
            const startPos = (_d = (_c = (_b = (_a = startPoint.entity) === null || _a === void 0 ? void 0 : _a.position) === null || _b === void 0 ? void 0 : _b.getValue) === null || _c === void 0 ? void 0 : _c.call(_b, now)) !== null && _d !== void 0 ? _d : startPoint.position;
            const endPos = (_h = (_g = (_f = (_e = endPoint.entity) === null || _e === void 0 ? void 0 : _e.position) === null || _f === void 0 ? void 0 : _f.getValue) === null || _g === void 0 ? void 0 : _g.call(_f, now)) !== null && _h !== void 0 ? _h : endPoint.position;
            return [startPos, endPos];
        };
        affected.forEach(({ segment, startPoint, endPoint }) => {
            var _a;
            if (!((_a = segment.lineEntity) === null || _a === void 0 ? void 0 : _a.polyline))
                return;
            segment.lineEntity.polyline.positions = new Cesium.CallbackProperty(() => livePositions(startPoint, endPoint), false);
        });
        return () => {
            affected.forEach(({ segment, startPoint, endPoint }) => {
                var _a;
                if (!((_a = segment.lineEntity) === null || _a === void 0 ? void 0 : _a.polyline))
                    return;
                segment.lineEntity.polyline.positions = new Cesium.ConstantProperty(livePositions(startPoint, endPoint));
            });
        };
    }, [dragging]);
    useEffect(() => { updateDraggedPointRef.current = updateDraggedPoint; }, [updateDraggedPoint]);
    useEffect(() => { updateDraggedPendingPointRef.current = updateDraggedPendingPoint; }, [updateDraggedPendingPoint]);
    const handleMapHover = useCallback((event) => {
        if (!viewer || viewer.isDestroyed())
            return;
        // Flag the drag as "moved" once the cursor travels far enough so mouseUp
        // knows it was a drag, not a click.
        if (mouseDownPosRef.current) {
            const dx = event.endPosition.x - mouseDownPosRef.current.x;
            const dy = event.endPosition.y - mouseDownPosRef.current.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance > 5) {
                dragMovedRef.current = true;
            }
        }
        // Handle pending path point dragging
        if (draggingPendingRef.current && pendingPathRef.current) {
            hidePhantom();
            const position = pickTerrainPositionRef.current(event.endPosition);
            if (position && isPositionInConstraintRef.current(position)) {
                updateDraggedPendingPointRef.current(position);
            }
            return;
        }
        // Handle completed path point dragging
        if (draggingRef.current) {
            hidePhantom();
            const position = pickTerrainPositionRef.current(event.endPosition);
            if (position && isPositionInConstraintRef.current(position)) {
                updateDraggedPointRef.current(position);
            }
            return;
        }
        // Detect line hover for crosshair (only when not dragging)
        if (!draggingPendingRef.current) {
            const pickedObject = viewer.scene.pick(event.endPosition);
            const pickedEntity = pickedObject === null || pickedObject === void 0 ? void 0 : pickedObject.id;
            // GE Pro-style vertex hover detection (gated to path tool active).
            if (isActiveRef.current) {
                let foundVertex = null;
                if (pickedEntity) {
                    const pending = pendingPathRef.current;
                    if (pending) {
                        for (const point of pending.points) {
                            if (pickedEntity === point.entity) {
                                foundVertex = { kind: 'pending', pointId: point.id };
                                break;
                            }
                        }
                    }
                    if (!foundVertex) {
                        outer: for (const [pathId, path] of pathsRef.current.entries()) {
                            for (const point of path.points) {
                                if (pickedEntity === point.entity) {
                                    foundVertex = { kind: 'completed', pathId, pointId: point.id };
                                    break outer;
                                }
                            }
                        }
                    }
                }
                const prevHover = hoveredVertexRef.current;
                const same = prevHover && foundVertex && prevHover.kind === foundVertex.kind &&
                    (foundVertex.kind === 'pending'
                        ? prevHover.kind === 'pending' && prevHover.pointId === foundVertex.pointId
                        : prevHover.kind === 'completed' && prevHover.pathId === foundVertex.pathId && prevHover.pointId === foundVertex.pointId);
                if (!same)
                    setHoveredVertex(foundVertex);
                if (foundVertex) {
                    // Vertex took the hover; don't also report a line hover, and hide
                    // the phantom — otherwise it can briefly remain visible behind/
                    // overlapping the vertex when the cursor crosses from line to node.
                    setHoveredPath(null);
                    hidePhantom();
                    return;
                }
            }
            if (pickedObject === null || pickedObject === void 0 ? void 0 : pickedObject.id) {
                const entity = pickedObject.id;
                for (const [pathId, path] of pathsRef.current.entries()) {
                    for (let segIdx = 0; segIdx < path.segments.length; segIdx++) {
                        if (entity === path.segments[segIdx].lineEntity) {
                            const position = pickTerrainPositionRef.current(event.endPosition);
                            if (!position) {
                                setHoveredPath(null);
                                hidePhantom();
                                return;
                            }
                            let cumulativeDist = 0;
                            for (let i = 0; i < segIdx; i++) {
                                cumulativeDist += path.segments[i].distanceMeters;
                            }
                            const startPt = path.points.find(p => p.id === path.segments[segIdx].startPointId);
                            if (startPt) {
                                cumulativeDist += Cesium.Cartesian3.distance(startPt.position, position);
                            }
                            setHoveredPath({
                                pathId,
                                segmentIndex: segIdx,
                                elevation: undefined,
                                distanceFromStart: cumulativeDist,
                                screenPosition: { x: event.endPosition.x, y: event.endPosition.y },
                                unit: path.displayUnit
                            });
                            // Phantom insert-node ghost — only shown when this exact
                            // path is in edit mode. Outside edit mode, hovering a
                            // segment shouldn't advertise insertion; clicking the
                            // segment should just open the path's options dialog
                            // (which has the Edit button to enter edit mode).
                            if (editingPathIdRef.current === pathId) {
                                const phantom = ensurePhantomEntity();
                                if (phantom) {
                                    phantom.position = new Cesium.ConstantPositionProperty(position);
                                    if (phantom.point) {
                                        phantom.point.color = new Cesium.ConstantProperty(Cesium.Color.fromCssColorString(path.color).withAlpha(0.55));
                                    }
                                    phantom.show = true;
                                    phantomTargetRef.current = { pathId, segmentIndex: segIdx, position };
                                }
                            }
                            else {
                                hidePhantom();
                            }
                            return;
                        }
                    }
                }
            }
            setHoveredPath(null);
            hidePhantom();
        }
    }, [viewer, ensurePhantomEntity, hidePhantom]);
    const handleMapMouseDown = useCallback((event) => {
        if (!viewer || viewer.isDestroyed())
            return false;
        // Read-only view of a saved path: no node drag / move. Click Edit to modify.
        if (viewLockedRef.current)
            return false;
        const pickedObject = viewer.scene.pick(event.position);
        if (!(pickedObject === null || pickedObject === void 0 ? void 0 : pickedObject.id))
            return false;
        const entity = pickedObject.id;
        // Check pending path points first (allows dragging while drawing)
        const pending = pendingPathRef.current;
        if (pending) {
            for (let i = 0; i < pending.points.length; i++) {
                const point = pending.points[i];
                if (entity === point.entity) {
                    setDraggingPending({
                        pointIndex: i,
                        originalPosition: point.position.clone()
                    });
                    mouseDownPosRef.current = { x: event.position.x, y: event.position.y };
                    dragMovedRef.current = false;
                    disableCameraControls(viewer);
                    return true;
                }
            }
        }
        // Check completed path points
        for (const [pathId, path] of pathsRef.current.entries()) {
            for (let pointIndex = 0; pointIndex < path.points.length; pointIndex++) {
                const point = path.points[pointIndex];
                if (entity === point.entity) {
                    setDragging({
                        pathId,
                        pointId: point.id,
                        originalPosition: point.position.clone()
                    });
                    mouseDownPosRef.current = { x: event.position.x, y: event.position.y };
                    dragMovedRef.current = false;
                    disableCameraControls(viewer);
                    return true;
                }
            }
        }
        return false;
    }, [viewer]);
    const handleMapMouseUp = useCallback(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        if (!viewer)
            return;
        mouseDownPosRef.current = null;
        // Handle pending path point drag end
        const currentDraggingPending = draggingPendingRef.current;
        const currentPending = pendingPathRef.current;
        if (currentDraggingPending && currentPending) {
            enableCameraControls(viewer);
            // Click (no movement) → toggle vertex selection instead of finalizing drag.
            if (!dragMovedRef.current) {
                const point = currentPending.points[currentDraggingPending.pointIndex];
                if (point) {
                    const prev = selectedDrawingVertexRef.current;
                    if ((prev === null || prev === void 0 ? void 0 : prev.kind) === 'pending' && prev.pointId === point.id) {
                        setSelectedDrawingVertex(null);
                    }
                    else {
                        setSelectedDrawingVertex({ kind: 'pending', pointId: point.id });
                    }
                }
                setDraggingPending(null);
                dragMovedRef.current = false;
                return;
            }
            const pointIndex = currentDraggingPending.pointIndex;
            const point = currentPending.points[pointIndex];
            const newPosition = ((_c = (_b = (_a = point.entity) === null || _a === void 0 ? void 0 : _a.position) === null || _b === void 0 ? void 0 : _b.getValue) === null || _c === void 0 ? void 0 : _c.call(_b, Cesium.JulianDate.now())) || point.position;
            setPendingPath(prev => {
                if (!prev)
                    return null;
                const updatedPoints = [...prev.points];
                updatedPoints[pointIndex] = { ...updatedPoints[pointIndex], position: newPosition };
                const updatedSegments = prev.segments.map((segment, idx) => {
                    const startPtIdx = updatedPoints.findIndex(p => p.id === segment.startPointId);
                    const endPtIdx = updatedPoints.findIndex(p => p.id === segment.endPointId);
                    if (startPtIdx !== -1 && endPtIdx !== -1) {
                        const dist = Cesium.Cartesian3.distance(updatedPoints[startPtIdx].position, updatedPoints[endPtIdx].position);
                        return { ...segment, distanceMeters: dist };
                    }
                    return segment;
                });
                return { points: updatedPoints, segments: updatedSegments };
            });
            setDraggingPending(null);
            return;
        }
        // Handle completed path point drag end
        const currentDragging = draggingRef.current;
        if (!currentDragging)
            return;
        enableCameraControls(viewer);
        // Click (no movement) → toggle vertex selection instead of finalizing drag.
        if (!dragMovedRef.current) {
            const prev = selectedDrawingVertexRef.current;
            const path = pathsRef.current.get(currentDragging.pathId);
            const isFirst = ((_d = path === null || path === void 0 ? void 0 : path.points[0]) === null || _d === void 0 ? void 0 : _d.id) === currentDragging.pointId;
            const isLast = ((_e = path === null || path === void 0 ? void 0 : path.points[path.points.length - 1]) === null || _e === void 0 ? void 0 : _e.id) === currentDragging.pointId;
            if ((prev === null || prev === void 0 ? void 0 : prev.kind) === 'completed' && prev.pathId === currentDragging.pathId && prev.pointId === currentDragging.pointId) {
                setSelectedDrawingVertex(null);
                // Toggling off an endpoint clears its anchor too.
                if (isFirst || isLast)
                    setExtendAnchorEnd(null);
            }
            else {
                setSelectedDrawingVertex({ kind: 'completed', pathId: currentDragging.pathId, pointId: currentDragging.pointId });
                // Clicking an endpoint anchors extend on that end. Middle clicks leave it.
                if (isFirst)
                    setExtendAnchorEnd('first');
                else if (isLast)
                    setExtendAnchorEnd('last');
            }
            setDragging(null);
            dragMovedRef.current = false;
            return;
        }
        const path = pathsRef.current.get(currentDragging.pathId);
        if (path && projectId) {
            const pointIndex = path.points.findIndex(p => p.id === currentDragging.pointId);
            if (pointIndex !== -1) {
                const newPosition = (_h = (_g = (_f = path.points[pointIndex].entity) === null || _f === void 0 ? void 0 : _f.position) === null || _g === void 0 ? void 0 : _g.getValue) === null || _h === void 0 ? void 0 : _h.call(_g, Cesium.JulianDate.now());
                if (newPosition) {
                    // The early-return above (dragMovedRef) guarantees this is a real
                    // move, not a click — so snapshot the pre-drag geometry for undo.
                    pushUndoSnapshot();
                    setPaths(prev => {
                        const newMap = new Map(prev);
                        const existingPath = newMap.get(currentDragging.pathId);
                        if (existingPath) {
                            const updatedPoints = [...existingPath.points];
                            updatedPoints[pointIndex] = { ...updatedPoints[pointIndex], position: newPosition };
                            const updatedSegments = existingPath.segments.map(segment => {
                                if (segment.startPointId === currentDragging.pointId || segment.endPointId === currentDragging.pointId) {
                                    return { ...segment, elevationProfile: undefined };
                                }
                                return segment;
                            });
                            const totalDistance = updatedSegments.reduce((sum, s, idx) => {
                                const startPt = updatedPoints.find(p => p.id === s.startPointId);
                                const endPt = updatedPoints.find(p => p.id === s.endPointId);
                                if (startPt && endPt) {
                                    const dist = Cesium.Cartesian3.distance(startPt.position, endPt.position);
                                    updatedSegments[idx] = { ...updatedSegments[idx], distanceMeters: dist };
                                    return sum + dist;
                                }
                                return sum + s.distanceMeters;
                            }, 0);
                            newMap.set(currentDragging.pathId, {
                                ...existingPath,
                                points: updatedPoints,
                                segments: updatedSegments,
                                totalDistanceMeters: totalDistance,
                                totalElevationProfile: undefined
                            });
                        }
                        return newMap;
                    });
                    const gen = ++elevationFetchGenRef.current;
                    path.segments.forEach((segment, index) => {
                        if (segment.startPointId === currentDragging.pointId || segment.endPointId === currentDragging.pointId) {
                            const startPoint = path.points.find(p => p.id === segment.startPointId);
                            const endPoint = path.points.find(p => p.id === segment.endPointId);
                            const startPos = segment.startPointId === currentDragging.pointId ? newPosition : startPoint === null || startPoint === void 0 ? void 0 : startPoint.position;
                            const endPos = segment.endPointId === currentDragging.pointId ? newPosition : endPoint === null || endPoint === void 0 ? void 0 : endPoint.position;
                            if (startPos && endPos) {
                                fetchSegmentElevationProfile(startPos, endPos, gen)
                                    .then(profile => {
                                    setPaths(prev => {
                                        const newMap = new Map(prev);
                                        const existingPath = newMap.get(currentDragging.pathId);
                                        if (existingPath) {
                                            const updatedSegments = [...existingPath.segments];
                                            updatedSegments[index] = { ...updatedSegments[index], elevationProfile: profile || null };
                                            const totalProfile = combinedElevationProfiles(updatedSegments);
                                            newMap.set(currentDragging.pathId, {
                                                ...existingPath,
                                                segments: updatedSegments,
                                                totalElevationProfile: totalProfile
                                            });
                                        }
                                        return newMap;
                                    });
                                });
                            }
                        }
                    });
                }
            }
        }
        setDragging(null);
    }, [viewer, projectId, fetchSegmentElevationProfile, combinedElevationProfiles, pushUndoSnapshot]);
    const cancelPending = useCallback(() => {
        if (!pendingPath || !dataSourceRef.current)
            return;
        pendingPath.points.forEach(point => {
            var _a;
            if (point.entity) {
                (_a = dataSourceRef.current) === null || _a === void 0 ? void 0 : _a.entities.remove(point.entity);
            }
        });
        pendingPath.segments.forEach(segment => {
            var _a, _b;
            if (segment.lineEntity) {
                (_a = dataSourceRef.current) === null || _a === void 0 ? void 0 : _a.entities.remove(segment.lineEntity);
            }
            if (segment.labelEntity) {
                (_b = dataSourceRef.current) === null || _b === void 0 ? void 0 : _b.entities.remove(segment.labelEntity);
            }
        });
        setPendingPath(null);
        setSelectedDrawingVertex(null);
        setExtendAnchorEnd(null);
    }, [pendingPath]);
    // Visual highlight for the currently-selected drawing/edit vertex.
    // Mutates the entity's point.color to a high-contrast yellow ring; restores on deselect.
    useEffect(() => {
        const sel = selectedDrawingVertex;
        if (!sel)
            return;
        const findEntity = () => {
            var _a, _b;
            if (sel.kind === 'pending') {
                const pending = pendingPathRef.current;
                return (_a = pending === null || pending === void 0 ? void 0 : pending.points.find(p => p.id === sel.pointId)) === null || _a === void 0 ? void 0 : _a.entity;
            }
            const path = pathsRef.current.get(sel.pathId);
            return (_b = path === null || path === void 0 ? void 0 : path.points.find(p => p.id === sel.pointId)) === null || _b === void 0 ? void 0 : _b.entity;
        };
        const entity = findEntity();
        if (!(entity === null || entity === void 0 ? void 0 : entity.point))
            return;
        const originalColor = entity.point.color;
        const originalOutlineColor = entity.point.outlineColor;
        const originalOutlineWidth = entity.point.outlineWidth;
        entity.point.color = new Cesium.ConstantProperty(Cesium.Color.YELLOW);
        entity.point.outlineColor = new Cesium.ConstantProperty(Cesium.Color.BLACK);
        entity.point.outlineWidth = new Cesium.ConstantProperty(3);
        return () => {
            if (entity.point) {
                entity.point.color = originalColor;
                entity.point.outlineColor = originalOutlineColor;
                entity.point.outlineWidth = originalOutlineWidth;
            }
        };
    }, [selectedDrawingVertex]);
    // GE Pro-style hover highlight (lime green). Selection wins if both target the same vertex.
    // Re-runs when selection changes too: ensures hover cleanup restores the TRUE original
    // before selection captures it as "original" — otherwise selection's cleanup would later
    // restore to the green hover-mutation value.
    useEffect(() => {
        const hov = hoveredVertex;
        if (!hov)
            return;
        const sel = selectedDrawingVertex;
        const sameAsSelected = sel && sel.kind === hov.kind && (hov.kind === 'pending'
            ? sel.kind === 'pending' && sel.pointId === hov.pointId
            : sel.kind === 'completed' && sel.pathId === hov.pathId && sel.pointId === hov.pointId);
        if (sameAsSelected)
            return;
        const findEntity = () => {
            var _a, _b;
            if (hov.kind === 'pending') {
                const pending = pendingPathRef.current;
                return (_a = pending === null || pending === void 0 ? void 0 : pending.points.find(p => p.id === hov.pointId)) === null || _a === void 0 ? void 0 : _a.entity;
            }
            const path = pathsRef.current.get(hov.pathId);
            return (_b = path === null || path === void 0 ? void 0 : path.points.find(p => p.id === hov.pointId)) === null || _b === void 0 ? void 0 : _b.entity;
        };
        const entity = findEntity();
        if (!(entity === null || entity === void 0 ? void 0 : entity.point))
            return;
        const originalColor = entity.point.color;
        const originalOutlineColor = entity.point.outlineColor;
        const originalOutlineWidth = entity.point.outlineWidth;
        entity.point.color = new Cesium.ConstantProperty(Cesium.Color.LIME);
        entity.point.outlineColor = new Cesium.ConstantProperty(Cesium.Color.BLACK);
        entity.point.outlineWidth = new Cesium.ConstantProperty(3);
        return () => {
            if (entity.point) {
                entity.point.color = originalColor;
                entity.point.outlineColor = originalOutlineColor;
                entity.point.outlineWidth = originalOutlineWidth;
            }
        };
    }, [hoveredVertex, selectedDrawingVertex]);
    // Delete the currently-selected drawing/edit vertex (Delete key or panel button).
    const deleteSelectedDrawingVertex = useCallback(() => {
        var _a, _b, _c, _d;
        const sel = selectedDrawingVertexRef.current;
        if (!sel)
            return;
        if (sel.kind === 'completed') {
            const path = pathsRef.current.get(sel.pathId);
            if (!path)
                return;
            const idx = path.points.findIndex(p => p.id === sel.pointId);
            if (idx < 0)
                return;
            // Promote the trailing neighbour (or the new head when deleting the
            // first node) so a run of deletes doesn't need a re-click per node.
            // At <= 2 points deleteNode removes the whole path, so clear instead.
            const trailing = path.points.length > 2
                ? path.points[idx > 0 ? idx - 1 : 1]
                : null;
            (_a = deleteNodeRef.current) === null || _a === void 0 ? void 0 : _a.call(deleteNodeRef, sel.pathId, idx);
            setSelectedDrawingVertex(trailing
                ? { kind: 'completed', pathId: sel.pathId, pointId: trailing.id }
                : null);
            return;
        }
        // Pending path: remove the vertex and rebuild the segments
        const pending = pendingPathRef.current;
        if (!pending || !dataSourceRef.current)
            return;
        const idx = pending.points.findIndex(p => p.id === sel.pointId);
        if (idx < 0)
            return;
        const ds = dataSourceRef.current;
        // Single point left: just remove it and tear the pending path down quietly.
        // (Don't call cancelPending — it iterates all points/segments and is overkill;
        // also avoids accidental key auto-repeat collapsing the whole drawing.)
        if (pending.points.length === 1) {
            const removedOnly = pending.points[0];
            if (removedOnly.entity)
                ds.entities.remove(removedOnly.entity);
            setPendingPath(null);
            setSelectedDrawingVertex(null);
            setExtendAnchorEnd(null);
            return;
        }
        const removed = pending.points[idx];
        if (removed.entity)
            ds.entities.remove(removed.entity);
        pending.segments.forEach(seg => {
            if (seg.lineEntity)
                ds.entities.remove(seg.lineEntity);
            if (seg.labelEntity)
                ds.entities.remove(seg.labelEntity);
        });
        const newPoints = pending.points.filter((_, i) => i !== idx);
        const newSegments = [];
        for (let i = 0; i < newPoints.length - 1; i++) {
            const start = newPoints[i];
            const end = newPoints[i + 1];
            const dist = Cesium.Cartesian3.distance(start.position, end.position);
            const lineEntity = (_b = createLineEntityRef.current) === null || _b === void 0 ? void 0 : _b.call(createLineEntityRef, start.position, end.position, DEFAULT_PATH_COLOR);
            const labelEntity = (_d = (_c = createLabelEntityRef.current) === null || _c === void 0 ? void 0 : _c.call(createLabelEntityRef, start.position, end.position, dist, defaultUnitRef.current)) !== null && _d !== void 0 ? _d : undefined;
            newSegments.push({
                id: generateSegmentId(),
                startPointId: start.id,
                endPointId: end.id,
                distanceMeters: dist,
                lineEntity: lineEntity !== null && lineEntity !== void 0 ? lineEntity : undefined,
                labelEntity
            });
        }
        setPendingPath({ points: newPoints, segments: newSegments });
        // Promote the new last vertex to "active" so yellow + Delete-node persist.
        if (newPoints.length > 0) {
            const newLast = newPoints[newPoints.length - 1];
            setSelectedDrawingVertex({ kind: 'pending', pointId: newLast.id });
        }
        else {
            setSelectedDrawingVertex(null);
        }
    }, []);
    // Esc clears selection (in addition to the existing pending-path Escape handler).
    useEffect(() => {
        const onKey = (e) => {
            if (!selectedDrawingVertexRef.current)
                return;
            if (e.key === 'Escape') {
                setSelectedDrawingVertex(null);
                setExtendAnchorEnd(null);
            }
            else if (e.key === 'Delete' || e.key === 'Backspace') {
                // Don't fire if focus is in an input/textarea
                const target = e.target;
                if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable))
                    return;
                e.preventDefault();
                deleteSelectedDrawingVertex();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [deleteSelectedDrawingVertex]);
    const undoLastPoint = useCallback(() => {
        if (!pendingPath || !dataSourceRef.current)
            return;
        if (pendingPath.points.length <= 1) {
            cancelPending();
            return;
        }
        const lastPoint = pendingPath.points[pendingPath.points.length - 1];
        const lastSegment = pendingPath.segments[pendingPath.segments.length - 1];
        if (lastPoint.entity) {
            dataSourceRef.current.entities.remove(lastPoint.entity);
        }
        if (lastSegment) {
            if (lastSegment.lineEntity) {
                dataSourceRef.current.entities.remove(lastSegment.lineEntity);
            }
            if (lastSegment.labelEntity) {
                dataSourceRef.current.entities.remove(lastSegment.labelEntity);
            }
        }
        setPendingPath(prev => {
            if (!prev)
                return null;
            return {
                points: prev.points.slice(0, -1),
                segments: prev.segments.slice(0, -1)
            };
        });
        // Promote the new last vertex to "active" so yellow + Delete-node persist.
        const newLast = pendingPath.points[pendingPath.points.length - 2];
        if (newLast) {
            setSelectedDrawingVertex({ kind: 'pending', pointId: newLast.id });
        }
        else {
            setSelectedDrawingVertex(null);
        }
    }, [pendingPath, cancelPending]);
    const startExtendPath = useCallback((pathId) => {
        const path = paths.get(pathId);
        if (!path || path.points.length < 2)
            return;
        // fromEnd will be determined dynamically when user clicks to add a point
        setExtendingPath({ pathId, fromEnd: true });
        setSelectedPath(null);
    }, [paths]);
    const stopExtending = useCallback(() => {
        setExtendingPath(null);
    }, []);
    const removePath = useCallback((pathId) => {
        const path = paths.get(pathId);
        if (!path || !dataSourceRef.current)
            return;
        pushUndoSnapshot();
        path.points.forEach(point => {
            var _a;
            if (point.entity) {
                (_a = dataSourceRef.current) === null || _a === void 0 ? void 0 : _a.entities.remove(point.entity);
            }
        });
        path.segments.forEach(segment => {
            var _a, _b;
            if (segment.lineEntity) {
                (_a = dataSourceRef.current) === null || _a === void 0 ? void 0 : _a.entities.remove(segment.lineEntity);
            }
            if (segment.labelEntity) {
                (_b = dataSourceRef.current) === null || _b === void 0 ? void 0 : _b.entities.remove(segment.labelEntity);
            }
        });
        if (path.bufferEntities) {
            path.bufferEntities.forEach(e => { var _a; return (_a = dataSourceRef.current) === null || _a === void 0 ? void 0 : _a.entities.remove(e); });
        }
        setPaths(prev => {
            const newMap = new Map(prev);
            newMap.delete(pathId);
            return newMap;
        });
        setPoppedOutProfiles(prev => prev.filter(p => p.pathId !== pathId));
        if ((selectedPath === null || selectedPath === void 0 ? void 0 : selectedPath.pathId) === pathId) {
            setSelectedPath(null);
        }
    }, [paths, selectedPath, pushUndoSnapshot]);
    const deleteNode = useCallback((pathId, pointIndex) => {
        const path = paths.get(pathId);
        if (!path || !dataSourceRef.current)
            return;
        // Can't delete if path would have less than 2 points
        if (path.points.length <= 2) {
            // Just remove the entire path instead (removePath snapshots).
            removePath(pathId);
            return;
        }
        pushUndoSnapshot();
        const pointToDelete = path.points[pointIndex];
        // Remove the point entity
        if (pointToDelete.entity) {
            dataSourceRef.current.entities.remove(pointToDelete.entity);
        }
        // Find segments connected to this point
        const prevSegmentIdx = path.segments.findIndex(s => s.endPointId === pointToDelete.id);
        const nextSegmentIdx = path.segments.findIndex(s => s.startPointId === pointToDelete.id);
        // Remove connected segment entities
        if (prevSegmentIdx !== -1) {
            const prevSegment = path.segments[prevSegmentIdx];
            if (prevSegment.lineEntity)
                dataSourceRef.current.entities.remove(prevSegment.lineEntity);
            if (prevSegment.labelEntity)
                dataSourceRef.current.entities.remove(prevSegment.labelEntity);
        }
        if (nextSegmentIdx !== -1) {
            const nextSegment = path.segments[nextSegmentIdx];
            if (nextSegment.lineEntity)
                dataSourceRef.current.entities.remove(nextSegment.lineEntity);
            if (nextSegment.labelEntity)
                dataSourceRef.current.entities.remove(nextSegment.labelEntity);
        }
        // Build new points array without the deleted point
        const newPoints = path.points.filter((_, idx) => idx !== pointIndex);
        // Build new segments array
        let newSegments = [];
        if (pointIndex === 0) {
            // Deleting first point - just remove the first segment
            newSegments = path.segments.slice(1);
            // Update first point to be start point style
            const newFirstPoint = newPoints[0];
            if (newFirstPoint.entity && dataSourceRef.current) {
                dataSourceRef.current.entities.remove(newFirstPoint.entity);
                newFirstPoint.entity = createPointEntity(newFirstPoint.position, path.color, true, newPoints.length === 1) || undefined;
            }
        }
        else if (pointIndex === path.points.length - 1) {
            // Deleting last point - just remove the last segment
            newSegments = path.segments.slice(0, -1);
            // Update last point to be end point style
            const newLastPoint = newPoints[newPoints.length - 1];
            if (newLastPoint.entity && dataSourceRef.current) {
                dataSourceRef.current.entities.remove(newLastPoint.entity);
                newLastPoint.entity = createPointEntity(newLastPoint.position, path.color, newPoints.length === 1, true) || undefined;
            }
        }
        else {
            // Deleting middle point - need to reconnect
            const prevPoint = newPoints[pointIndex - 1];
            const nextPoint = newPoints[pointIndex]; // This is now at the deleted point's index
            // Create new connecting segment
            const newDistance = Cesium.Cartesian3.distance(prevPoint.position, nextPoint.position);
            const newLineEntity = createLineEntity(prevPoint.position, nextPoint.position, path.color, path.lineWidth);
            const newLabelEntity = createLabelEntity(prevPoint.position, nextPoint.position, newDistance, path.displayUnit) || undefined;
            const newSegment = {
                id: `seg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                startPointId: prevPoint.id,
                endPointId: nextPoint.id,
                distanceMeters: newDistance,
                lineEntity: newLineEntity || undefined,
                labelEntity: newLabelEntity
            };
            // Build segments: keep segments before prevSegment, add new segment, keep segments after nextSegment
            newSegments = [
                ...path.segments.slice(0, prevSegmentIdx),
                newSegment,
                ...path.segments.slice(nextSegmentIdx + 1)
            ];
        }
        // Calculate new total distance
        const newTotalDistance = newSegments.reduce((sum, seg) => sum + seg.distanceMeters, 0);
        // Update state
        setPaths(prev => {
            const newMap = new Map(prev);
            newMap.set(pathId, {
                ...path,
                points: newPoints,
                segments: newSegments,
                totalDistanceMeters: newTotalDistance,
                totalElevationProfile: undefined // Will need to recalculate
            });
            return newMap;
        });
        // Fetch elevation profile for the new connecting segment if it's a middle deletion
        if (pointIndex > 0 && pointIndex < path.points.length - 1 && projectId) {
            const gen = ++elevationFetchGenRef.current;
            const prevPoint = newPoints[pointIndex - 1];
            const nextPoint = newPoints[pointIndex];
            fetchSegmentElevationProfile(prevPoint.position, nextPoint.position, gen)
                .then(profile => {
                setPaths(prev => {
                    const newMap = new Map(prev);
                    const existingPath = newMap.get(pathId);
                    if (existingPath) {
                        const updatedSegments = [...existingPath.segments];
                        // Find the new segment we just created
                        const newSegIdx = updatedSegments.findIndex(s => s.startPointId === prevPoint.id && s.endPointId === nextPoint.id);
                        if (newSegIdx !== -1) {
                            updatedSegments[newSegIdx] = { ...updatedSegments[newSegIdx], elevationProfile: profile || null };
                            const totalProfile = combinedElevationProfiles(updatedSegments);
                            newMap.set(pathId, {
                                ...existingPath,
                                segments: updatedSegments,
                                totalElevationProfile: totalProfile
                            });
                        }
                    }
                    return newMap;
                });
            });
        }
    }, [paths, removePath, createPointEntity, createLineEntity, createLabelEntity, showSegmentLabels, projectId, fetchSegmentElevationProfile, combinedElevationProfiles, pushUndoSnapshot]);
    // Sync the deleteNode and cancelPending refs (declared up top) with the actual functions
    // so deleteSelectedDrawingVertex can call them via ref without a circular dep.
    useEffect(() => { deleteNodeRef.current = deleteNode; }, [deleteNode]);
    useEffect(() => { cancelPendingRef.current = cancelPending; }, [cancelPending]);
    const updatePathColor = useCallback((pathId, color) => {
        const path = paths.get(pathId);
        if (!path)
            return;
        path.points.forEach((point, idx) => {
            var _a;
            if ((_a = point.entity) === null || _a === void 0 ? void 0 : _a.point) {
                const isFirst = idx === 0;
                const isLast = idx === path.points.length - 1;
                if (isFirst) {
                    point.entity.point.color = new Cesium.ConstantProperty(Cesium.Color.fromCssColorString(color));
                    point.entity.point.outlineColor = new Cesium.ConstantProperty(Cesium.Color.BLACK);
                }
                else if (isLast) {
                    point.entity.point.color = new Cesium.ConstantProperty(Cesium.Color.BLACK);
                    point.entity.point.outlineColor = new Cesium.ConstantProperty(Cesium.Color.fromCssColorString(color));
                }
                else {
                    point.entity.point.color = new Cesium.ConstantProperty(Cesium.Color.fromCssColorString(color).withAlpha(0.5));
                    point.entity.point.outlineColor = new Cesium.ConstantProperty(Cesium.Color.fromCssColorString(color));
                }
            }
        });
        path.segments.forEach(segment => {
            var _a;
            if ((_a = segment.lineEntity) === null || _a === void 0 ? void 0 : _a.polyline) {
                segment.lineEntity.polyline.material = new Cesium.ColorMaterialProperty(Cesium.Color.fromCssColorString(color));
            }
        });
        if (path.bufferEntities) {
            const newColor = Cesium.Color.fromCssColorString(color);
            const fillMaterial = new Cesium.ColorMaterialProperty(newColor.withAlpha(0.3));
            const outlineColorProp = new Cesium.ConstantProperty(newColor.withAlpha(0.8));
            path.bufferEntities.forEach(e => {
                if (e.polygon) {
                    e.polygon.material = fillMaterial;
                    e.polygon.outlineColor = outlineColorProp;
                }
            });
        }
        setPaths(prev => {
            const newMap = new Map(prev);
            newMap.set(pathId, { ...path, color });
            return newMap;
        });
    }, [paths]);
    const updatePathLineWidth = useCallback((pathId, width) => {
        const path = paths.get(pathId);
        if (!path)
            return;
        path.segments.forEach(segment => {
            var _a;
            if ((_a = segment.lineEntity) === null || _a === void 0 ? void 0 : _a.polyline) {
                segment.lineEntity.polyline.width = viewer
                    ? makePolylineWidthProperty(width, viewer)
                    : new Cesium.ConstantProperty(width);
            }
        });
        setPaths(prev => {
            const newMap = new Map(prev);
            const existing = newMap.get(pathId);
            if (existing)
                newMap.set(pathId, { ...existing, lineWidth: width });
            return newMap;
        });
    }, [paths, viewer]);
    const updatePathName = useCallback((pathId, name) => {
        setPaths(prev => {
            const newMap = new Map(prev);
            const path = newMap.get(pathId);
            if (path) {
                newMap.set(pathId, { ...path, name });
            }
            return newMap;
        });
    }, []);
    // Pin/unpin this path's on-map segment-distance labels. The visibility effect
    // above reacts to the flag; persistence picks it up via the paths map.
    const setPathShowMeasurements = useCallback((pathId, show) => {
        setPaths(prev => {
            const newMap = new Map(prev);
            const path = newMap.get(pathId);
            if (path) {
                newMap.set(pathId, { ...path, showMeasurements: show });
            }
            return newMap;
        });
    }, []);
    const updatePathUnit = useCallback((pathId, unit) => {
        const path = paths.get(pathId);
        if (!path)
            return;
        path.segments.forEach(segment => {
            var _a;
            if ((_a = segment.labelEntity) === null || _a === void 0 ? void 0 : _a.label) {
                segment.labelEntity.label.text = new Cesium.ConstantProperty(formatDistance(segment.distanceMeters, unit));
            }
        });
        setPaths(prev => {
            const newMap = new Map(prev);
            newMap.set(pathId, { ...path, displayUnit: unit });
            return newMap;
        });
        setDefaultUnit(unit);
    }, [paths]);
    // Public action: change the path's colour-by mode. Drives the cross-section
    // chart (which layer's values colour the curve, which range to normalise
    // TWI against, etc.). The on-map polyline is unaffected — it stays in the
    // path's chosen `color`. We tried colouring the polyline too, but the
    // segment-line swap fought the in-flight node-drag preview and felt
    // broken; the chart is the right surface for "decision support" data.
    const updatePathColorBy = useCallback((pathId, mode) => {
        setPaths(prev => {
            const path = prev.get(pathId);
            if (!path)
                return prev;
            const next = new Map(prev);
            next.set(pathId, { ...path, colorBy: mode });
            return next;
        });
    }, []);
    // Watch paths for "colorBy active + segment profile state changed" and
    // rebuild the colored overlay automatically. This is what makes the
    // overlay feel live: as elevation profiles arrive segment-by-segment from
    // the backend, the overlay fills in without explicit calls scattered
    // across every fetch handler. Skips work when the freshness fingerprint
    // matches the last build.
    const updatePathOpacity = useCallback((pathId, opacity) => {
        var _a;
        const path = paths.get(pathId);
        if (!path)
            return;
        const baseColor = Cesium.Color.fromCssColorString(path.color);
        path.segments.forEach(segment => {
            var _a, _b;
            if ((_a = segment.lineEntity) === null || _a === void 0 ? void 0 : _a.polyline) {
                segment.lineEntity.polyline.material = new Cesium.ColorMaterialProperty(baseColor.withAlpha(opacity));
            }
            if ((_b = segment.labelEntity) === null || _b === void 0 ? void 0 : _b.label) {
                segment.labelEntity.label.fillColor = new Cesium.ConstantProperty(Cesium.Color.WHITE.withAlpha(opacity));
                segment.labelEntity.label.outlineColor = new Cesium.ConstantProperty(Cesium.Color.BLACK.withAlpha(opacity));
            }
        });
        path.points.forEach((point, idx) => {
            var _a;
            if ((_a = point.entity) === null || _a === void 0 ? void 0 : _a.point) {
                const isFirst = idx === 0;
                const isLast = idx === path.points.length - 1;
                if (isFirst) {
                    point.entity.point.color = new Cesium.ConstantProperty(baseColor.withAlpha(opacity));
                    point.entity.point.outlineColor = new Cesium.ConstantProperty(Cesium.Color.BLACK.withAlpha(opacity));
                }
                else if (isLast) {
                    point.entity.point.color = new Cesium.ConstantProperty(Cesium.Color.BLACK.withAlpha(opacity));
                    point.entity.point.outlineColor = new Cesium.ConstantProperty(baseColor.withAlpha(opacity));
                }
                else {
                    point.entity.point.color = new Cesium.ConstantProperty(baseColor.withAlpha(opacity * 0.5));
                    point.entity.point.outlineColor = new Cesium.ConstantProperty(baseColor.withAlpha(opacity));
                }
            }
        });
        if ((_a = path.totalLabelEntity) === null || _a === void 0 ? void 0 : _a.label) {
            path.totalLabelEntity.label.fillColor = new Cesium.ConstantProperty(Cesium.Color.WHITE.withAlpha(opacity));
            path.totalLabelEntity.label.outlineColor = new Cesium.ConstantProperty(Cesium.Color.BLACK.withAlpha(opacity));
        }
        if (path.bufferEntities) {
            const fillMaterial = new Cesium.ColorMaterialProperty(baseColor.withAlpha(0.3 * opacity));
            const outlineColorProp = new Cesium.ConstantProperty(baseColor.withAlpha(0.8 * opacity));
            path.bufferEntities.forEach(e => {
                if (e.polygon) {
                    e.polygon.material = fillMaterial;
                    e.polygon.outlineColor = outlineColorProp;
                }
            });
        }
        setItemOpacities(prev => {
            const newMap = new Map(prev);
            newMap.set(pathId, opacity);
            return newMap;
        });
    }, [paths]);
    const getItemOpacity = (id) => {
        var _a;
        return (_a = itemOpacities.get(id)) !== null && _a !== void 0 ? _a : 1;
    };
    const popOutProfile = useCallback((pathId, segmentIndex, position) => {
        const existing = poppedOutProfiles.find(p => p.pathId === pathId && p.segmentIndex === segmentIndex);
        if (existing)
            return;
        setPoppedOutProfiles(prev => [...prev, { pathId, segmentIndex, position }]);
    }, [poppedOutProfiles]);
    const closePopOutProfile = useCallback((pathId, segmentIndex) => {
        setPoppedOutProfiles(prev => prev.filter(p => !(p.pathId === pathId && p.segmentIndex === segmentIndex)));
    }, []);
    const dismissSelection = useCallback(() => {
        setSelectedPath(null);
        setExtendAnchorEnd(null);
        // A leftover hidden flag must never suppress the next path's dialog.
        setPathDialogHidden(false);
    }, []);
    // X on the path dialog. If the path still has a popped-out profile, only
    // hide the dialog — the selection (and with it the node handles, drag,
    // insert, and empty-click extend) stays live so the path remains editable
    // from just the popout. With no popout, fall back to a full deselect.
    const closePathDialog = useCallback(() => {
        const sel = selectedPathRef.current;
        if (sel && poppedOutProfiles.some(p => p.pathId === sel.pathId)) {
            setPathDialogHidden(true);
            return;
        }
        dismissSelection();
    }, [poppedOutProfiles, dismissSelection]);
    // Hidden-dialog invariant: the hidden state is only legitimate while the
    // selected path still has a popout (that's what's "showing" it). If the
    // last popout goes away for any reason — closed by the user, path removed,
    // project switch, scope clear — fully deselect so no invisible sticky
    // selection keeps routing map clicks into the path. And a hidden flag with
    // no selection at all is stale; clear it so the next dialog isn't suppressed.
    useEffect(() => {
        if (!pathDialogHidden)
            return;
        if (selectedPath && !poppedOutProfiles.some(p => p.pathId === selectedPath.pathId)) {
            dismissSelection();
        }
        else if (!selectedPath) {
            setPathDialogHidden(false);
        }
    }, [pathDialogHidden, selectedPath, poppedOutProfiles, dismissSelection]);
    const selectPathById = useCallback((pathId, screenPosition) => {
        // No existence check: caller may invoke right after a state-updating create
        // (setPaths queues, isn't visible yet). Dialog rendering already guards on .get(id).
        setPathDialogHidden(false);
        setSelectedPath({
            pathId,
            screenPosition: screenPosition !== null && screenPosition !== void 0 ? screenPosition : { x: window.innerWidth / 2, y: window.innerHeight / 2 }
        });
    }, []);
    const clearAllPaths = useCallback(() => {
        if (!dataSourceRef.current)
            return;
        paths.forEach(path => {
            path.points.forEach(point => {
                var _a;
                if (point.entity) {
                    (_a = dataSourceRef.current) === null || _a === void 0 ? void 0 : _a.entities.remove(point.entity);
                }
            });
            path.segments.forEach(segment => {
                var _a, _b;
                if (segment.lineEntity) {
                    (_a = dataSourceRef.current) === null || _a === void 0 ? void 0 : _a.entities.remove(segment.lineEntity);
                }
                if (segment.labelEntity) {
                    (_b = dataSourceRef.current) === null || _b === void 0 ? void 0 : _b.entities.remove(segment.labelEntity);
                }
            });
            if (path.bufferEntities) {
                path.bufferEntities.forEach(e => { var _a; return (_a = dataSourceRef.current) === null || _a === void 0 ? void 0 : _a.entities.remove(e); });
            }
        });
        setPaths(new Map());
        setPoppedOutProfiles([]);
        setSelectedPath(null);
    }, [paths]);
    const restoreFromConfig = useCallback((configs, startHidden = false) => {
        if (!dataSourceRef.current || !configs || configs.length === 0)
            return;
        configs.forEach(config => {
            var _a;
            const points = config.points.map((pt, idx) => {
                const cartesian = Cesium.Cartesian3.fromDegrees(pt.longitude, pt.latitude);
                const isFirst = idx === 0;
                const isLast = idx === config.points.length - 1;
                const entity = createPointEntity(cartesian, config.color, isFirst, isLast);
                if (startHidden && entity) {
                    entity.show = false;
                }
                return {
                    id: generatePointId(),
                    position: cartesian,
                    entity: entity || undefined
                };
            });
            const segments = [];
            for (let i = 0; i < points.length - 1; i++) {
                const startPoint = points[i];
                const endPoint = points[i + 1];
                const distance = Cesium.Cartesian3.distance(startPoint.position, endPoint.position);
                const lineEntity = createLineEntity(startPoint.position, endPoint.position, config.color, config.lineWidth);
                if (startHidden && lineEntity) {
                    lineEntity.show = false;
                }
                const labelEntity = createLabelEntity(startPoint.position, endPoint.position, distance, config.unit) || undefined;
                if (startHidden && labelEntity) {
                    labelEntity.show = false;
                }
                segments.push({
                    id: generateSegmentId(),
                    startPointId: startPoint.id,
                    endPointId: endPoint.id,
                    distanceMeters: distance,
                    lineEntity: lineEntity || undefined,
                    labelEntity
                });
            }
            const totalDistance = segments.reduce((sum, s) => sum + s.distanceMeters, 0);
            const path = {
                id: config.id,
                name: config.name,
                points,
                segments,
                totalDistanceMeters: totalDistance,
                color: config.color,
                displayUnit: config.unit || defaultUnit,
                projectId: config.projectId,
                showMeasurements: (_a = config.showMeasurements) !== null && _a !== void 0 ? _a : false,
                lineWidth: config.lineWidth,
                colorBy: config.colorBy
            };
            setPaths(prev => {
                const newMap = new Map(prev);
                newMap.set(config.id, path);
                return newMap;
            });
            if (projectId) {
                const gen = ++elevationFetchGenRef.current;
                segments.forEach((segment, index) => {
                    const startPoint = points.find(p => p.id === segment.startPointId);
                    const endPoint = points.find(p => p.id === segment.endPointId);
                    if (startPoint && endPoint) {
                        fetchSegmentElevationProfile(startPoint.position, endPoint.position, gen)
                            .then(profile => {
                            setPaths(prev => {
                                const newMap = new Map(prev);
                                const existingPath = newMap.get(config.id);
                                if (existingPath) {
                                    const updatedSegments = [...existingPath.segments];
                                    updatedSegments[index] = { ...updatedSegments[index], elevationProfile: profile || null };
                                    const totalProfile = combinedElevationProfiles(updatedSegments);
                                    newMap.set(config.id, {
                                        ...existingPath,
                                        segments: updatedSegments,
                                        totalElevationProfile: totalProfile
                                    });
                                }
                                return newMap;
                            });
                        });
                    }
                });
            }
        });
    }, [createPointEntity, createLineEntity, createLabelEntity, showSegmentLabels, defaultUnit, projectId, fetchSegmentElevationProfile, combinedElevationProfiles]);
    const createPathFromCoordinates = useCallback((coordinates, name, color, lineWidth) => {
        if (!dataSourceRef.current || coordinates.length < 2)
            return null;
        const pathId = generatePathId();
        const pathName = name || nextDefaultPathName(paths);
        const pathColor = color || DEFAULT_PATH_COLOR;
        const points = coordinates.map((coord, idx) => {
            const cartesian = Cesium.Cartesian3.fromDegrees(coord.lng, coord.lat);
            const isFirst = idx === 0;
            const isLast = idx === coordinates.length - 1;
            const entity = createPointEntity(cartesian, pathColor, isFirst, isLast);
            return {
                id: generatePointId(),
                position: cartesian,
                entity: entity || undefined
            };
        });
        const segments = [];
        for (let i = 0; i < points.length - 1; i++) {
            const startPoint = points[i];
            const endPoint = points[i + 1];
            const distance = Cesium.Cartesian3.distance(startPoint.position, endPoint.position);
            const lineEntity = createLineEntity(startPoint.position, endPoint.position, pathColor, lineWidth);
            const labelEntity = createLabelEntity(startPoint.position, endPoint.position, distance, defaultUnit) || undefined;
            segments.push({
                id: generateSegmentId(),
                startPointId: startPoint.id,
                endPointId: endPoint.id,
                distanceMeters: distance,
                lineEntity: lineEntity || undefined,
                labelEntity
            });
        }
        const totalDistance = segments.reduce((sum, s) => sum + s.distanceMeters, 0);
        const newPath = {
            id: pathId,
            name: pathName,
            points,
            segments,
            totalDistanceMeters: totalDistance,
            color: pathColor,
            displayUnit: defaultUnit,
            projectId: projectId || undefined,
            lineWidth,
            showMeasurements: showSegmentLabelsRef.current
        };
        setPaths(prev => {
            const newMap = new Map(prev);
            newMap.set(pathId, newPath);
            return newMap;
        });
        if (projectId) {
            const gen = ++elevationFetchGenRef.current;
            segments.forEach((segment, index) => {
                const startPoint = points.find(p => p.id === segment.startPointId);
                const endPoint = points.find(p => p.id === segment.endPointId);
                if (startPoint && endPoint) {
                    fetchSegmentElevationProfile(startPoint.position, endPoint.position, gen)
                        .then(profile => {
                        setPaths(prev => {
                            const newMap = new Map(prev);
                            const existingPath = newMap.get(pathId);
                            if (existingPath) {
                                const updatedSegments = [...existingPath.segments];
                                updatedSegments[index] = { ...updatedSegments[index], elevationProfile: profile || null };
                                const totalProfile = combinedElevationProfiles(updatedSegments);
                                newMap.set(pathId, {
                                    ...existingPath,
                                    segments: updatedSegments,
                                    totalElevationProfile: totalProfile
                                });
                            }
                            return newMap;
                        });
                    });
                }
            });
        }
        return pathId;
    }, [paths, createPointEntity, createLineEntity, createLabelEntity, showSegmentLabels, defaultUnit, projectId, fetchSegmentElevationProfile, combinedElevationProfiles]);
    useEffect(() => {
        if (!isActive && pendingPath) {
            cancelPending();
        }
    }, [isActive, pendingPath, cancelPending]);
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && pendingPath) {
                cancelPending();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [pendingPath, cancelPending]);
    useEffect(() => {
        if (selectedPath && !isItemVisible(selectedPath.pathId)) {
            setSelectedPath(null);
        }
    }, [selectedPath, isItemVisible]);
    const highlightSegment = useCallback((pathId, segmentIndex) => {
        var _a, _b;
        // Clear previous highlight
        if (glowEntityRef.current && dataSourceRef.current) {
            dataSourceRef.current.entities.remove(glowEntityRef.current);
            glowEntityRef.current = null;
        }
        // Reset previous highlighted segment width (use ref to avoid dependency cycle)
        const prevHighlight = highlightedSegmentRef.current;
        if (prevHighlight) {
            const prevPath = paths.get(prevHighlight.pathId);
            if (prevPath) {
                const prevSegment = prevPath.segments[prevHighlight.segmentIndex];
                if ((_a = prevSegment === null || prevSegment === void 0 ? void 0 : prevSegment.lineEntity) === null || _a === void 0 ? void 0 : _a.polyline) {
                    prevSegment.lineEntity.polyline.width = new Cesium.ConstantProperty(3);
                }
            }
        }
        // If no segment to highlight (total view), just clear
        if (segmentIndex === null) {
            highlightedSegmentRef.current = null;
            setHighlightedSegment(null);
            return;
        }
        const path = paths.get(pathId);
        if (!path || !dataSourceRef.current) {
            highlightedSegmentRef.current = null;
            setHighlightedSegment(null);
            return;
        }
        const segment = path.segments[segmentIndex];
        if (!segment) {
            highlightedSegmentRef.current = null;
            setHighlightedSegment(null);
            return;
        }
        // Make the segment line thicker
        if ((_b = segment.lineEntity) === null || _b === void 0 ? void 0 : _b.polyline) {
            segment.lineEntity.polyline.width = new Cesium.ConstantProperty(5);
        }
        // Get the segment positions for glow effect
        const startPoint = path.points.find(p => p.id === segment.startPointId);
        const endPoint = path.points.find(p => p.id === segment.endPointId);
        if (startPoint && endPoint) {
            glowEntityRef.current = createGlowHighlight(dataSourceRef.current, [startPoint.position, endPoint.position], path.color);
        }
        highlightedSegmentRef.current = { pathId, segmentIndex };
        setHighlightedSegment({ pathId, segmentIndex });
    }, [paths]);
    const clearSegmentHighlight = useCallback(() => {
        highlightSegment('', null);
    }, [highlightSegment]);
    const serializeForStorage = useCallback(() => {
        const existingJson = localStorage.getItem(STORAGE_KEY);
        return serializePathsForStorage(paths, projectId, existingJson);
    }, [paths, projectId]);
    const restoreFromStorage = useCallback((stored) => {
        if (!projectId)
            return;
        const configs = deserializeStoredPaths(stored, projectId);
        restoreFromConfig(configs, true);
    }, [restoreFromConfig, projectId]);
    const { saveToStorage } = useLocalStoragePersistence({
        storageKey: STORAGE_KEY,
        serialize: serializeForStorage,
        restore: restoreFromStorage,
        isReady: !!dataSourceRef.current && !!projectId,
        resetKey: projectId
    });
    useEffect(() => {
        saveToStorage();
    }, [paths, saveToStorage]);
    // Rebuild every path from a geometry snapshot (used by undo/redo). Clears the
    // live set first so removed paths disappear and moved ones snap back. The
    // current selection, popped-out profiles, and dialog-hidden flag are
    // preserved across the rebuild (path ids survive snapshots) so undoing an
    // edit mid-session — dialog open or popout-only — doesn't kick the user
    // out of editing.
    const restoreGeometrySnapshot = useCallback((configs) => {
        const keep = selectedPathRef.current;
        const keepPopouts = poppedOutProfiles.filter(p => configs.some(c => c.id === p.pathId));
        const keepDialogHidden = pathDialogHidden;
        clearAllPaths();
        restoreFromConfig(configs);
        setPoppedOutProfiles(keepPopouts);
        if (keep && configs.some(c => c.id === keep.pathId)) {
            selectPathById(keep.pathId, keep.screenPosition);
            setPathDialogHidden(keepDialogHidden);
        }
    }, [clearAllPaths, restoreFromConfig, selectPathById, poppedOutProfiles, pathDialogHidden]);
    const undoEdit = useCallback(() => {
        if (undoStackRef.current.length === 0)
            return;
        redoStackRef.current.push(captureConfigs());
        const snap = undoStackRef.current.pop();
        restoreGeometrySnapshot(snap);
    }, [captureConfigs, restoreGeometrySnapshot]);
    const redoEdit = useCallback(() => {
        if (redoStackRef.current.length === 0)
            return;
        undoStackRef.current.push(captureConfigs());
        const snap = redoStackRef.current.pop();
        restoreGeometrySnapshot(snap);
    }, [captureConfigs, restoreGeometrySnapshot]);
    // Cmd/Ctrl+Z undo, Cmd/Ctrl+Shift+Z redo — scoped to when the path tool is
    // armed or a path is selected, so it doesn't hijack the shortcut elsewhere.
    // Suppressed while typing.
    useEffect(() => {
        const onKey = (e) => {
            if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'z')
                return;
            if (!isActive && !selectedPath)
                return;
            const target = e.target;
            const inEditable = !!target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
            if (inEditable)
                return;
            e.preventDefault();
            if (e.shiftKey)
                redoEdit();
            else
                undoEdit();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isActive, selectedPath, undoEdit, redoEdit]);
    // Subscribe to scope events for clear and restore operations
    useEventSubscribe('scope:clearAll', () => {
        clearAllPaths();
    }, [clearAllPaths]);
    useEventSubscribe('scope:restore', (payload) => {
        if (payload.paths && payload.paths.length > 0) {
            restoreFromConfig(payload.paths);
        }
    }, [restoreFromConfig]);
    const handleRightClickDelete = useCallback((event) => {
        if (!viewer || viewer.isDestroyed())
            return false;
        // Try scene.pick() first
        const pickedObject = viewer.scene.pick(event.position);
        if (pickedObject === null || pickedObject === void 0 ? void 0 : pickedObject.id) {
            const entity = pickedObject.id;
            for (const [pathId, path] of paths.entries()) {
                for (let i = 0; i < path.points.length; i++) {
                    if (entity === path.points[i].entity) {
                        deleteNode(pathId, i);
                        return true;
                    }
                }
            }
        }
        // Fallback: proximity-based search in screen space
        const SCREEN_THRESHOLD = 25;
        let closestDist = SCREEN_THRESHOLD;
        let closestPathId = null;
        let closestPointIndex = -1;
        for (const [pathId, path] of paths.entries()) {
            for (let i = 0; i < path.points.length; i++) {
                const pointScreenPos = Cesium.SceneTransforms.worldToWindowCoordinates(viewer.scene, path.points[i].position);
                if (!pointScreenPos)
                    continue;
                const dx = event.position.x - pointScreenPos.x;
                const dy = event.position.y - pointScreenPos.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                if (dist < closestDist) {
                    closestDist = dist;
                    closestPathId = pathId;
                    closestPointIndex = i;
                }
            }
        }
        if (closestPathId !== null && closestPointIndex >= 0) {
            deleteNode(closestPathId, closestPointIndex);
            return true;
        }
        return false;
    }, [viewer, paths, deleteNode]);
    const isEntityDraggable = useCallback((entity) => {
        // Check completed path point entities
        for (const [, path] of paths.entries()) {
            for (const point of path.points) {
                if (entity === point.entity)
                    return true;
            }
        }
        // Check pending path point entities
        if (pendingPath) {
            for (const point of pendingPath.points) {
                if (entity === point.entity)
                    return true;
            }
        }
        return false;
    }, [paths, pendingPath]);
    return {
        paths,
        pendingPath,
        selectedPath,
        hoveredPath,
        dragging,
        draggingPending,
        extendingPath,
        poppedOutProfiles,
        defaultUnit,
        setDefaultUnit,
        showSegmentLabels,
        highlightedSegment,
        isVisible,
        handleMapClick,
        handleMapDoubleClick,
        handleMapHover,
        handleMapMouseDown,
        handleMapMouseUp,
        checkExistingPathClick,
        handleRightClickDelete,
        selectedDrawingVertex,
        deleteSelectedDrawingVertex,
        clearSelectedDrawingVertex: () => setSelectedDrawingVertex(null),
        isEntityDraggable,
        finishPath,
        applyBuffer,
        removeBuffer,
        restoreBuffer,
        cancelPending,
        undoLastPoint,
        removePath,
        deleteNode,
        updatePathColor,
        updatePathColorBy,
        updatePathLineWidth,
        updatePathName,
        updatePathUnit,
        setPathShowMeasurements,
        updatePathOpacity,
        getItemOpacity,
        popOutProfile,
        closePopOutProfile,
        dismissSelection,
        pathDialogHidden,
        closePathDialog,
        selectPathById,
        clearAllPaths,
        restoreFromConfig,
        startExtendPath,
        stopExtending,
        createPathFromCoordinates,
        setShowSegmentLabels,
        highlightSegment,
        clearSegmentHighlight,
        hideAll,
        showAll,
        toggleItemVisibility,
        isItemVisible,
        pathCount: paths.size
    };
};
